// strategyMeta.js — Paper 전략 메타데이터 (운영 + 페기)
//
// PaperStrategyDetailPage 와 PaperRetiredPage 에서 strategy_id 로 lookup 한다.
// paper-trader 백엔드는 KPI/포지션/거래 등 동적 데이터만 응답하므로,
// 사람이 읽는 "이 전략이 무엇인지" 설명은 frontend 에 hardcode 한다.
//
// 작성 원칙 (BacktestResearch CLAUDE.md 0번 규칙 준용):
//   - 비전문가도 이해 가능하도록 한국어로 작성
//   - 전문 용어 첫 등장 시 괄호 안에 짧은 설명
//   - 한 줄 summary + 메커니즘 + 진입/청산
//
// DAS-126: 폐기 상태/사유/최종통계는 백엔드(paper_retired_strategies)가
// 영속화하므로 이 파일에는 더 이상 retired* 필드를 두지 않는다. 폐기 카드는
// 백엔드 레코드를 mergeRetiredWithMeta() 로 아래 설명과 머지해 렌더한다.
//
// schema (설명 전용):
//   {
//     name:           표시 이름 (e.g. "H927 매크로 Risk-Off")
//     paradigm:       분류 라벨 (Macro / Liq / Vol5m / Basis / Cross-coin / Variant)
//     summary:        한 줄 요약 (카드/리스트용)
//     mechanism:      어떤 데이터로 시그널을 만드는지 (2~3 문장)
//     entryExit:      진입/청산 규칙 (1~2 문장)
//   }

import { formatLabeledAt } from "../utils/time";

const STRATEGY_META = {
  // ─────────────────────────────────────────────────────────
  // 운영 (Active)
  // ─────────────────────────────────────────────────────────

  gex_btc_front: {
    name: "H2122 GEX BTC 프론트만기 감마 페이드",
    paradigm: "Options GEX (dealer gamma)",
    summary:
      "BTC 근월(7일 내 만기) 옵션의 딜러 감마노출(GEX)이 높은 국면(딜러가 변동성을 억눌러 가격이 평균회귀하는 장세)에서 직전 가격 움직임을 역추세(fade)로 진입. 8시간 보유, BTC 전용, maker(지정가) 체결.",
    mechanism:
      "GEX(Gamma Exposure, 옵션 딜러가 헷지를 위해 떠안은 감마 노출 총합)는 Deribit BTC 옵션 체인에서 만기 7일 이내(DTE≤7) 종목만 모아 Σ(감마×미결제약정×현물²×부호; 콜 +, 풋 −)로 계산한다. GEX가 크게 양수면 딜러가 '롱 감마' 상태라 상승엔 매도·하락엔 매수로 헷지 → 변동성이 억눌리고 가격이 평균회귀하는 국면이 된다. 이 GEX를 최근 72시간 대비 z-score(gz, 평소 대비 표준편차 거리)로 정규화하고, 1시간 수익률의 48시간 z-score(mz)로 직전 모멘텀 세기를 잰다. Deribit 옵션 라이브 피드를 paper-trader에 신규 구축해 매 정시 GEX를 갱신한다(BCR-125).",
    entryExit:
      "gz≥0.5(딜러 롱감마=변동성 억제 국면) AND |mz|≥1.0(직전 모멘텀이 충분히 강함) → 모멘텀 반대 방향으로 fade 진입(mz>0이면 숏, mz<0이면 롱). 8시간 경과 시 시간청산(TP/SL 없음, 백테스트의 고정보유 horizon과 일치). 비용 maker 2bps+슬리피지 2bps. 백테스트(재검증) BTC hold8 @4bps TR PF 1.85 / OOS PF 1.66, 거래당 +19~25bps. caveat: BTC 전용(ETH 옵션은 얕아 NO-GO)·집중도(PF_ex_top3≈1.0)·71일 단기표본. paper 검증 중.",
  },

  gex_v2: {
    name: "H2159 GEX 감마 페이드 + 비대칭 추세게이트",
    paradigm: "Options GEX (dealer gamma) + trend gate",
    summary:
      "위 gex_btc_front(감마 페이드)를 그대로 쓰되, 상승장에서 무턱대고 숏(하락 베팅)을 잡다가 물리는 문제를 고친 개선판. '지금 가격이 최근 평균 아래로 내려온 하락 흐름일 때만' 숏을 허용하고, 롱(상승 베팅)은 조건 없이 허용한다. BTC 전용, 8시간 보유, maker(지정가) 체결.",
    mechanism:
      "신호 계산은 gex_btc_front와 동일하다 — Deribit BTC 근월(만기 7일 내) 옵션에서 GEX(Gamma Exposure, 옵션 딜러가 헷지로 떠안은 감마 노출 총합)를 구해 72시간 z-score(gz, 평소 대비 표준편차 거리)로, 1시간 수익률을 48시간 z-score(mz)로 정규화한다. 여기에 '비대칭 추세게이트'를 새로 얹는다: 현물 종가의 48시간 이동평균(SMA48h, 최근 48시간 평균가)을 계산해, 숏 신호는 '현재가 ≤ SMA48h'(가격이 평균 아래=하락/횡보 흐름)일 때만 통과시키고, 상승 흐름(현재가 > SMA48h)에서는 숏을 건너뛴다. 롱 신호는 추세와 무관하게 항상 통과. 원전략이 7월 BTC 랠리에서 숏이 전량 물려 손실난 것을, 이 게이트로 상승장 숏만 골라 차단해 교정했다. 재시작 직후 SMA를 아직 못 구한 콜드스타트에는 숏을 보수적으로 스킵한다.",
    entryExit:
      "gz≥0.5 AND |mz|≥1.0 → 모멘텀 반대 방향 fade 진입. 단 숏은 현재가≤SMA48h(하락추세)일 때만 실제 진입, 상승추세면 스킵. 롱은 무조건 진입. 8시간 경과 시 시간청산(TP/SL 없음). 비용 maker 2bps+슬리피지 2bps. 재백테스트 GO: OOS PF(총수익/총손실) 1.52 / 숏 PF 3.35 / PF_ex_top1(최대 단일 승리거래 1건 제외) 1.39, 7월 랠리 숏 6건 중 4건 스킵. caveat: BTC 전용·관측배포(증액 없이 검증만, 거래 30건 도달 시 KILL 게이트로 재판정). 라이브 진입마다 regime/spot/sma48/gate_pass를 기록해 게이트가 실제로 작동하는지 검증 중.",
  },

  h2121: {
    name: "H2121 베이시스-z 역추세 + ML 메타라벨",
    paradigm: "Basis (spot-perp) + ML meta-label",
    summary:
      "현물-선물 가격 괴리(베이시스)가 평소 대비 극단으로 벌어진 코인을 반대 방향으로 역추세(fade) 진입하되, 머신러닝(ML) 분류기가 '이 거래가 수익날 확률'을 매겨 높은 것만 골라 실행한다. 13코인 15분봉, 2시간 보유, maker(지정가) 체결.",
    mechanism:
      "베이시스(basis, 무기한선물가와 현물가의 차이 비율 = (선물−현물)/현물)를 최근 24시간(96개 15분봉) 대비 z-score(평소 대비 표준편차 거리)로 정규화해 |z|≥2.5인 극단 괴리를 트리거로 삼는다(선물 프리미엄/디스카운트가 과도 → 평균회귀 기대). 트리거 시점의 12개 피처(베이시스 z·강도, 직전 수익률, 변동성·거래량 z, 실현변동성, 시간대, fade 방향, 동시발화 코인수 n_simul, BTC 시장맥락 등)를 HistGradientBoosting(트리 기반 머신러닝) 분류기에 넣어 거래 성공확률 P를 예측한다. 모델은 과거 트리거 전체로 학습(walk-forward 검증). ML 필터가 base 대비 Profit Factor(PF, 총수익/총손실)를 1.02→1.71로 끌어올렸고, 재학습주기(1/3/7/14일)·확률임계값 스윕에 안정적(robust). 이 repo에서 supervised ML을 전략에 처음 적용한 사례.",
    entryExit:
      "|basis_z|≥2.5 트리거 중 ML 예측확률 P≥0.55 인 것만 진입(베이시스가 +극단=선물 비쌈이면 숏, −극단이면 롱). 2시간(8봉) 경과 시 시간청산(TP/SL 없음, 백테스트 보유기간과 일치). 비용 maker 2bps+슬리피지 2bps. 백테스트 meta PF 1.71, OOS+HO 부트스트랩 P(평균수익>0)=99.6%, Sharpe(연) 3.61. caveat: 데이터 3개월·6월 성과 견인·4월 약세 국면·소표본. BCR-124 paper 2주 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2123: {
    name: "H2123 크로스거래소 펀딩 divergence + ML 메타라벨",
    paradigm: "Cross-exchange funding divergence + ML meta-label",
    summary:
      "같은 코인의 바이낸스 펀딩비와 OKX 펀딩비 차이(divergence)가 평소 대비 극단으로 벌어지면 한쪽 거래소에 포지션이 쏠린 신호로 보고, 바이낸스 무기한선물을 반대 방향으로 역추세(fade) 진입한다. 머신러닝(ML) 분류기가 수익 확률이 높은 트리거만 골라 실행. 12코인 1시간봉, 24시간 보유, maker(지정가) 체결.",
    mechanism:
      "펀딩비(funding rate, 무기한선물 롱·숏이 8시간마다 주고받는 수수료 — 양수면 롱이 숏에게 지불=롱 쏠림)를 바이낸스·OKX 양쪽에서 받아 그 차이(BN−OKX)를 최근 7일(168시간) z-score(평소 대비 표준편차 거리, fz)로 정규화한다. |fz|가 클수록 한 거래소에 롱(또는 숏)이 과도하게 몰린 상태 → 평균회귀(되돌림) 기대. 트리거 시점의 15개 피처(펀딩 spread·z, 양 거래소 펀딩값, 가격 모멘텀 z, 변동성·거래량 z, OI 변화, 동시발화 코인수, BTC 맥락 등)를 HistGradientBoosting(트리 기반 ML) 분류기에 넣어 거래 성공확률 P를 예측한다. 메모리 H1013/H1017에서 이 신호의 forward 방향정확도 76~86%가 관측됐으나 비용에 막혀(cost-bound) 폐기됐던 것을, ML 메타라벨로 고확신 부분집합만 선별해 net 양수로 되살린 사례.",
    entryExit:
      "|fz|≥2.5 트리거 중 ML 예측확률 P≥0.55 인 것만 진입(BN 펀딩이 상대적으로 높으면=롱 쏠림 → 숏, 낮으면 롱). 24시간 경과 시 시간청산(TP/SL 없음). 비용 maker 2bps+슬리피지 2bps. 백테스트: base 자체 PF 1.82(전 구간 양수), ML 메타 PF 2.0(OOS 4.0/HO 3.2), 재학습주기(1/3/7/14일)·확률임계값 무관 PF 1.96~2.20 강건. caveat: 알트 편중(BTC·ETH는 음수, BCH·AVAX·ADA가 견인)·12코인·24h 보유·소표본. BCR-124 paper 2주 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2137: {
    name: "H2137 신규상장 숏 + ML 메타라벨 (바이낸스)",
    paradigm: "New-listing fade short + ML meta-label",
    summary:
      "바이낸스에 새로 상장된 무기한선물(perp) 코인은 상장 직후 과열 펌프 뒤 되돌림(fade)이 잦다. 상장 후 일정 시간 뒤 숏(하락 베팅) 진입하되, 머신러닝(ML) 분류기가 '이 상장이 잘 빠질 확률'을 매겨 높은 것만 골라 실행한다. 손절 +30%/익절 −30%/최대 7일 보유.",
    mechanism:
      "거래소 신규상장 시각(onboardDate)을 공개 API로 감지 → 상장 후 첫 5개 1시간봉이 마감되면 진입 시점까지 관측된 9개 피처(첫 구간 최대상승폭 pump·수익률·변동성·고저 범위·최대낙폭·고점대비 위치·가속도·거래량비·시간대)를 HistGradientBoosting(트리 기반 ML) 분류기에 넣어 fade 성공확률 P를 예측한다. 무 lookahead(미래정보 미사용) — 진입 시점에 실제로 알 수 있는 값만 피처로 사용(초기 검증에서 미래의 24h 펌프를 미리 본 버그를 잡아 수정). 진짜 OOS(2024-01~2025-09 학습 → 2025-09~2026-06 미학습 상장 평가)에서 전체 숏 평균 +2.38%를 ML 필터가 +7.52%로 끌어올림. 확률임계 0.50~0.65 안정·seed·walk-forward 4/4 일관.",
    entryExit:
      "신규상장 감지 → 첫 5개 1h봉 마감 후 ML 예측확률 P≥0.55 인 상장만 숏 진입(미만은 스킵). 가격 +30% 시 손절 / −30% 시 익절 / 최대 168시간(7일) 시간청산. 비용 round-trip 0.1%+펀딩. backtest OOS 메타 +7.52%/중앙+29.3%/승률65%. caveat: survivorship(상폐코인 제외→절대수익 상방편향, 상대개선은 robust)·신규상장 fade의 2026 약화(ML 필터가 방어)·소표본 ML 주기 재학습. paper 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2141: {
    name: "H2141 신규상장 숏 + ML 메타라벨 (Bybit)",
    paradigm: "New-listing fade short + ML meta-label (cross-exchange)",
    summary:
      "H2137과 동일한 신규상장 숏 전략을 독립 거래소 Bybit에서 운용. 신규 perp 상장 후 되돌림(fade) 숏 + ML 필터. 같은 엣지가 바이낸스·Bybit 양쪽에서 재현됨을 확인(거래소 무관 구조적 엣지).",
    mechanism:
      "Bybit v5 공개 API로 신규상장 시각(launchTime) 감지 → H2137과 동일한 9피처 + HGB(트리 기반 ML) 메타필터. Bybit 독립 데이터 390건 백테스트에서 전체 숏 평균 +2.66%(전 시기 4/4 양수, 집중도 무의존)로 바이낸스(+2.75%)와 거의 동일 → 신규상장 fade는 특정 거래소 특수성이 아니라 신규 perp 상장의 구조적 현상임을 입증. ML 필터 기여는 Bybit에선 marginal(최근 base가 이미 얇음, walk-forward 4/4 소폭 상회).",
    entryExit:
      "Bybit 신규상장 감지 → 첫 5개 1h봉 마감 후 ML P≥0.55 만 숏. 가격 +30% 손절 / −30% 익절 / 168시간 청산. backtest base +2.66%/중앙+6.19%/승률56%/누적+1038%. caveat: survivorship 상방편향·2026 약화·바이낸스판과 같은 전략군. paper 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2143: {
    name: "H2143 변동성 스파이크 역추세 + ML 메타라벨",
    paradigm: "Volatility-spike fade short + ML meta-label",
    summary:
      "5분봉 가격 변동폭(고저 레인지)이 평소 대비 급등(스파이크)하고 직전 1시간 상승한 코인을, 과열로 보고 반대로 숏(역추세 fade) 진입한다. ML 분류기가 잘 빠질 스파이크만 선별. 14코인, 지정가(maker), 타이트한 익절/손절, 30분 보유. 동시 진입 수 제한(꼬리위험 방어).",
    mechanism:
      "각 코인 5분봉의 (고가−저가)/종가 = 변동폭을 최근 24시간(288개 5분봉) 대비 z-score(평소 대비 표준편차 거리)로 정규화해 z≥1.5(상위 극단)이고 직전 1시간 상승했으며 BTC 변동성 국면(ATR 비율 0.9~2.0)일 때 트리거. 트리거 시점 7개 피처(변동폭 z·강도, 직전 수익률, 국면, 시간대, 코인, z×수익률)를 HistGradientBoosting(트리 기반 ML) 분류기로 성공확률 P 예측. ML 필터가 holdout 거래당 +0.077%→+0.107%, Profit Factor 2.2로 개선(확률임계 단조·walk-forward 3/3). ★중요 안전장치: 과거 동일 계열(h2020)이 라이브에서 시장 전체 동반 스파이크 시 14코인 동시 숏→추세 지속 시 일괄 손절로 큰 손실을 낸 전력 → 동시 보유 포지션을 최대 6개로 제한(gross-exposure 캡)해 상관 꼬리위험 방어.",
    entryExit:
      "변동폭 z≥1.5 + 직전 1h 상승 + 국면 통과 + ML P≥0.55 → 지정가 숏. 익절 −0.6%(부분익절 −0.4% 후 손절을 진입가로 이동=본전 방어), 손절 +0.6%, 30분 시간청산, 코인별 15분 쿨다운, 동시 오픈 ≤6. backtest 메타 거래당 +0.107%/PF 2.2(고빈도). caveat: per-trade 엣지 작음(체결·비용 민감, maker 전제)·상관 꼬리위험(추세일)→캡+모니터링 필수. paper 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2147: {
    name: "H2147 청산 캐스케이드 모멘텀 (OKX)",
    paradigm: "Liquidation-cascade momentum",
    summary:
      "무기한선물에서 강제 청산(liquidation)이 짧은 시간에 폭증(cascade)하면 그 청산 방향으로 가격이 더 밀린다고 보고 추세추종(momentum)으로 따라붙는다. 10코인, 5분봉, 30분 보유, OKX 청산 데이터 사용. (바이낸스 청산 피드는 아시아 지역 제한으로 끊겨 OKX로 전환.)",
    mechanism:
      "각 코인 5분봉의 청산 명목금액(notional=가격×수량) 합을 최근 24시간(288개 5분봉) 대비 z-score(평소 대비 표준편차 거리)로 정규화해 z≥2.0σ(상위 ~2.3% 청산 폭증=cascade)를 트리거로 삼는다. 순청산(net=숏청산합−롱청산합)의 부호로 방향 결정: net>0(숏 청산 우세=강제 매수 압력)이면 LONG, net<0(롱 청산 우세=강제 매도 압력)이면 SHORT로 모멘텀 진입. 청산 데이터는 OKX가 라이브 수집(BTC ~1772건/일). 백테스트(H2147, OKX 청산 10코인): Profit Factor(PF, 총수익/총손실) 1.97, 승률 57%, 10/10 코인 전부 양수(breadth 강함), 전·후반 모두 양수, 상위 코인 3개 제외해도 양수(소수 코인 의존 아님).",
    entryExit:
      "5분봉 청산 z≥2.0 cascade → net 방향 모멘텀 진입(LONG/SHORT). 익절 +1.0% / 손절 −0.6% / 30분 시간청산, 코인별 120분 쿨다운. 비용 round-trip 8bps(taker). ★동시 오픈 포지션 최대 6개 제한(gross-exposure 캡): 과거 동일계열(h2020)이 시장 전체 동반 청산 시 일괄 손절로 큰 손실을 낸 상관 꼬리위험 방어용. caveat: OKX 청산데이터 2026-03-21~ 단일 ~3개월 regime·고빈도. paper 검증 중(자본 1천만/거래당 100만 KRW).",
  },

  h2161: {
    name: "H2161 청산발 유동성진공 페이드 (LiqVac-Fade)",
    paradigm: "Microstructure (liquidity vacuum fade)",
    summary:
      "대형 강제청산(레버리지 포지션이 한꺼번에 시장가로 정리되는 것)이 터지면 마켓메이커(상시 호가를 대주는 참여자)가 순간적으로 위험을 피해 호가를 거둬들여 매수-매도 호가 차이(스프레드)가 확 벌어진다. 이는 '새로운 가격 정보'가 아니라 '일시적 유동성 공백'이라 보고, 스프레드가 다시 좁혀지는 짧은 구간(수 분~수십 분)에 청산 반대 방향으로 베팅한다. 2026-08-04 기준 데이터 피드 장애로 판정이 보류된 상태다.",
    mechanism:
      "바이낸스 선물의 BBO(최우선 매수/매도 호가, Best Bid/Offer) 데이터로 스프레드를 실시간 추적하다가, 청산 캐스케이드(짧은 시간에 몰린 강제 청산) 직후 스프레드가 평소 대비 4배 이상 벌어지고 그 확대가 90초 이내로 짧게 끝나는(=정보성 사건이 아닌 순수 유동성 공백) 경우만 트리거로 인정한다. 백테스트(v3)는 원설계인 바이낸스 청산 피드 대신 OKX 청산 데이터로 41일 검증했으나(BTC/ETH 5건, 전승), 실제 배포된 라이브 봇은 바이낸스 forceOrder(강제청산) 웹소켓 피드에 100% 의존하고 OKX로 넘어가는 폴백 코드가 없다 — 2026-08-04 함대 점검에서 이 피드가 배포 후 21일 전체에서 단 1건도 이벤트를 수신하지 못한 것(BTC/ETH 3주 무청산은 통계적으로 불가능)이 드러나 인프라 장애(P0)로 확정됐다.",
    entryExit:
      "청산 캐스케이드 후 스프레드 확대 지속 확인 완료 시점의 다음 관측 틱에서 taker(시장가) 진입, 청산 반대 방향. 스프레드 확대폭 26bps 이상만 진입(taker 왕복비용 12bps를 감안한 손익분기 마진). TP는 되돌림폭의 50%, 시간청산 위주. 백테스트(OKX 대체 소스, 41일): BTC 3건+ETH 2건 전승, 거래당 평균 net +11.4bps — 단 표본이 R3 게이트(n≥30)에 크게 못 미쳐 정식 판정 불가. 2026-08-04 함대 판정: 알파 문제가 아니라 인프라 장애로 판정 불능(dormant) — BN forceOrder 피드 수리(BacktestInfra/paper-trader WS 파서, P0)가 선행돼야 관측이 재개된다.",
  },
  h2167: {
    name: "H2167 업비트 거래유의·입출금중단 디스카운트 캡처 (Upbit 현물 롱 + 바이낸스 퍼프 숏)",
    paradigm: "Cross-venue discount basis (Upbit spot long / Binance perp short)",
    summary:
      "업비트가 어떤 코인을 '거래 유의 종목'으로 지정하고 입출금을 막으면, 그 코인은 업비트에서만 빗썸·바이낸스보다 5~59% 싸게 거래되는 일이 생긴다(코인을 밖으로 옮길 수 없어 차익거래자가 사라지고 가격이 고립됨). 입출금이 재개되거나 유의가 해제되면 하루 안에 가격이 다시 붙는다. 업비트에서 싸게 사고(현물 롱) 같은 수량을 바이낸스 무기한선물로 팔아(퍼프 숏) 코인 가격 변동을 상쇄한 뒤, 디스카운트가 사라질 때 양쪽을 동시에 청산한다.",
    mechanism:
      "BCR-144 선측정(2026-06~09, n=7 이벤트): TAIKO −59%(33일 지속, 입출금 재개일 즉시 수렴), STORJ −29%(4주), MANTRA −9→−36%, CARV −20%, RVN −3~−5%, SAND −4.7%, LSK −2~−5%. 전부 바이낸스 퍼프가 있어 헤지 가능, 헤지 펀딩 비용 이벤트당 0.3~9.5%. 디스카운트는 업비트 최우선 매도호가 ÷ 빗썸 최우선 매수호가 − 1(KRW↔KRW라 환율 불필요, level-0 호가 — 종가 기반은 빗썸 1원 틱 아티팩트로 무효). 플래그는 업비트 market_event.warning ∪ 빗썸 market_warning=CAUTION ∪ 최근 30일 공지(유의 지정/입출금 중단)로 공개 API만 사용. 핵심 안전장치는 펀딩 게이트: 최근 24시간 퍼프 펀딩 합(숏 지불분)×7일이 디스카운트의 절반을 넘으면 진입하지 않는다 — 2026-09-19 LSK가 −24% 디스카운트였지만 펀딩이 시간당 −0.3%(하루 −7%)라 3일이면 엣지가 사라지는 함정을 이 게이트가 차단했다.",
    entryExit:
      "진입: 디스카운트 ≤ −10% & 플래그 & 바이낸스 퍼프 TRADING & 펀딩 게이트 통과 & 거래지원 종료 공지 없음(전송 출구 없이는 손실이므로). 청산: CONV(디스카운트 −1% 이내 수렴) / FUND_STOP(누적 펀딩 지불이 진입 디스카운트의 60% 초과) / DELIST(거래지원 종료 공지 시 즉시, 빗썸 전송매도 대안값 병기) / PERP_GONE(퍼프 상폐) / 최대 45일. 비용 현물 5+3bps·퍼프 4+2bps 양방 + 펀딩, 퍼프 USDT 손익은 청산 시 KRW-USDT로 환산(환노출 정직 반영). caveat: 이벤트 빈도 낮음(3.5개월 7건)·수렴 미발생형(STORJ, 상폐까지 지속)은 전송 없이는 손실·퍼프 상폐 강제청산·펀딩 스파이크. paper 검증 중(레그당 100만 KRW, 동시 5개 캡, NUC tmux caution_disc).",
  },
  h2168: {
    name: "H2168 업비트 상장공지 퍼프 페이드 (BN 퍼프 숏 + 바스켓 롱, 72h)",
    paradigm: "New-listing event fade (Upbit notice → Binance perp short, basket-hedged)",
    summary:
      "업비트가 어떤 코인을 '신규 거래지원(상장)'한다고 공지하면 그 코인은 전 세계 거래소에서 한 번에 급등한다. 이 수요 충격은 일회성이라 공지 뒤 3일에 걸쳐 바이낸스 무기한선물 가격이 체계적으로 내려간다. 공지 감지 후 다음 정시 1시간봉에서 바이낸스 퍼프를 숏하고, 같은 금액의 유동성 상위 20종 바스켓을 롱해 시장 전체 움직임을 상쇄한 뒤 72시간 뒤(또는 손절 +30%) 양쪽을 동시에 청산한다.",
    mechanism:
      "BCR-147 라운드 18에서 업비트 공지 6년 캘린더(5,904건)와 binance.vision 2020~2026 1시간봉으로 선측정·백테스트했다. 공지 후 72시간 초과분 중앙 +444bps(6년 121 코인-이벤트, 공지 묶음 기준 에피소드 76~90), sim_h2168 공개캐시 결과 에피소드 90·거래당 net +413bps·군집 t 3.17·PF 1.97·승률 71%·분리창 3/3 양수·플라시보(공지 없는 동일 크기 급등) 20회 중 0회 본안 초과 — 게이트 7/7 통과. 기존 전략과의 분리: 라이브 업비트 상장 프런트런 봇은 '공지 감지 직후 수 초 롱'(반대 방향·초 단위), h2137/h2141은 '바이낸스 퍼프 자체의 신규 상장' 숏이라 퍼프 상장 30일 이상 지난 코인만 거래해 분리한다. 메커니즘 교정: '급등 되돌림'이 아니라 '공지 후 체계적 하락'이다 — 공지 전 24시간 급등이 없던 하위 분위도 +410bps였고, KRW 마켓 단독 공지(t 1.23)보다 BTC/USDT 마켓 공지(t 3.38)가 결과를 견인한다.",
    entryExit:
      "업비트 공지 API 2초 폴링 → '신규 거래지원' 최초 공지 감지(변경·연기·종료 공지 제외) → 바이낸스 퍼프 존재 & 상장 30일 이상 → 공지 시각 다음 정시(UTC) 1시간봉 시가에 퍼프 숏(공지 1건 = 1에피소드, 묶음 공지는 균등 분할) + 유동성 상위 20종 균등 롱 헤지 → 72시간 뒤 시간청산 / 가격 +30% 손절 / 퍼프 상폐 시 강제청산. 비용 taker 4+2bps 양측·양레그(왕복 약 24bps) + 양 레그 펀딩 실측. ⚠️ 엣지 감쇠: 연도별 net 2023 +965 → 2024 +726 → 2025 +244 → 2026 +153bps(t 0.44) — 최근 레짐에서는 유의하지 않아 **관측 전용(증액 금지, n≥30 KILL 게이트)**. 용량: 1분 거래대금 3% 룰로 이벤트당 중앙 약 4,400달러, 연 약 37건. paper 관측 중(공지당 100만 KRW, 동시 6개 캡, NUC tmux listing_fade).",
  },
  h2169: {
    name: "H2169 테이커 매수비중 주간 크로스섹션 롱숏 (바이낸스 퍼프 top100)",
    paradigm: "Cross-sectional taker-flow factor (weekly long/short, dollar-neutral)",
    summary:
      "'지난 일주일 동안 시장가로 사들인 비중이 유난히 높았던 코인은 사고, 유난히 낮았던 코인은 판다.' 바이낸스 무기한선물 30일 거래대금 상위 100종을 상장 경과일 5개 구간(60~120 / 120~180 / 180~365 / 365~730 / 730일+)으로 나눠 각 구간 안에서 직전 7일 테이커 매수비중(시장가 매수 거래대금 ÷ 전체 거래대금) 순위를 매기고, 전체 상위 20%를 롱·하위 20%를 숏으로 같은 금액 보유한다(달러중립). 매주 월요일 00:00 UTC에 교체하되 같은 방향으로 계속 뽑힌 코인은 그대로 둔다. 최근 7일 중 하루라도 펀딩 합이 ±0.72%를 넘은 코인은 제외한다(그런 코인의 수익은 전부 펀딩이고 가격 엣지는 0).",
    mechanism:
      "BCR-147 라운드 18에서 binance.vision 2020~2026 1시간봉 900종(상폐 포함, 생존편향 없음)으로 두 레인이 독립 구현해 10% 내 수렴했다. 테이커 매수비중은 공격적 매수 쏠림의 직접 척도이며, 기존 테이커 흐름 가설(H227/H057/H1051/H1762/H768)은 전부 분~시간 해상도 z-score나 반대 방향(fade)이라 분리된다. 가격 모멘텀에 회귀한 잔차로도 t 4.49가 남아 모멘텀 재포장이 아니고, 상장 경과일 구간 내 중립화 후에도 t 4.3이 남아 신규상장 코호트 팩터 재포장도 아니다. 성숙 코인(365일+)에선 가격 엣지가 0이고 남는 것이 펀딩(캐리)이라 구간 중립화와 극단 펀딩 밴드 제외를 설계에 넣었다. sim_h2169(6.75년, 342주): 봇과 동일 config 재현 net +110~149bps/주, t 3.2~4.1, e/c 7.9~15, PF 1.6, 플라시보 0/12, top-5 제외 양수, 수수료 1.5배 e/c 6. ⚠️ 가격 성분의 연도별 t는 2021년 이후 전부 1 미만이고 2022-23 창은 평탄, 2026은 82%가 펀딩 — 전구간 유의성은 2020~21이 만든다.",
    entryExit:
      "주 1회(UTC 월요일 00:00 봉 마감 후 다음 1시간봉) 리밸런스: 30일 거래대금 top100 ∧ 상장 60일+ ∧ 7일 펀딩 밴드 제외 → 5구간 내 테이커 매수비중 순위 → 상위 20% 롱 / 하위 20% 숏, 포지션당 25만 KRW(총 명목 약 1,000만 KRW). 같은 방향 유지 종목은 무회전(회전율 기반 비용 Σ|Δw|×왕복/2, 실측 주당 교체 56%). 비용 taker 4+2bps 양측 + 양 레그 펀딩 실측. 손절 없음(주간 시간청산). 백테스트 MDD −14~24%(자본 기준), BTC 베타 −0.1. **관측 전용·증액 금지** — KILL 기준은 전구간 평균이 아니라 구속 창(2022-23) 수준: 30주 누적 net이 음수면 KILL. paper 관측 중(NUC tmux xs_flow). 초기 바스켓은 기동 시 형성, 이후 월요일 주기.",
  },

  h2050: {
    name: "H2050 신규상장 Breakdown 숏 (Type A 동시신규)",
    paradigm: "New-listing breakdown short",
    summary:
      "Upbit+Binance 동시 신규상장 코인의 첫24h 저점 하회(breakdown) 시 Binance perp 숏 → -2% TP / 72h. fresh-perp 가드로 established 코인 제외(스퀴즈 회피).",
    mechanism:
      "신규상장은 첫날 FOMO 피크 후 분배매도로 하락. perp이 Upbit 상장과 함께 새로 출범한 '진짜 신규'만 거래(perp onboard ±48h). 첫24h 1m 저점 추적 → 24h 경과 후 1m 종가가 저점 하회 시 breakdown 진입. 김프≥10% 코인은 숏 스퀴즈 위험으로 제외. ARBITRAGE-135.",
    entryExit:
      "breakdown(24h후 종가<첫24h저점) → Binance perp 모의 숏. -2% TP(1m low) 또는 72h TIME 청산, 무손절. 백테스트(가드 적용) n=4 WR 100% +147bps Sharpe 1.92. dry_run paper 검증 중(소표본·단일 약세장 caveat).",
  },

  h2051: {
    name: "H2051 신규상장 Sell-the-news 숏 (Type B established)",
    paradigm: "New-listing sell-the-news short",
    summary:
      "Binance perp 기존 + Upbit 신규 KRW 상장 코인. 상장 전 선반영 펌프 후 상장 시점 Binance perp 숏 → 24h 되돌림. Type A가 가드로 버린 established 집단을 노리는 직교 전략.",
    mechanism:
      "한국 Upbit 상장을 선반영해 상장 전 24h Binance perp가 펌프 → 상장 실현 시 sell-the-news 되돌림. established(perp onboard≥48h前) + 상장 전 24h Binance 수익률 ≥5%(ex-ante) 코인을 상장 감지 즉시 숏. 펀딩 settlement 회계 포함. ARBITRAGE-136.",
    entryExit:
      "established+pre-pump≥5% → 상장시각 Binance perp 모의 숏, 24h TIME 청산(무손절, stop 옵션 off). 백테스트(펀딩 포함) n=8 WR 100% +1078bps Sharpe 1.75. dry_run paper 검증 중(소표본·단일 약세장 caveat).",
  },

  h1112: {
    name: "H1112 청산 캐스케이드 양방향",
    paradigm: "Liq",
    summary:
      "거래소 강제청산(liquidation)이 폭증할 때 가격이 잠깐 과도하게 움직이는 현상을 양쪽 모두 베팅. 4단 보강된 H1107 방법론.",
    mechanism:
      "특정 코인에서 5분 안에 청산 거래대금이 평소 대비 z-score 임계치 이상 급증하면 '청산 캐스케이드' 발생으로 판단. 1) 양방향 진입(롱 청산 폭증 → SHORT, 숏 청산 폭증 → LONG), 2) 부분청산, 3) 변동성 regime 필터, 4) z 임계치 완화 — 4단 보강.",
    entryExit:
      "liq z>1.5 이상이면 청산 방향 반대로 진입. 일부 익절 + 잔여 추적. 변동성 regime 이 낮으면 진입 차단.",
  },

  h1112_lenient: {
    name: "H1112 청산 캐스케이드 (느슨한 진입)",
    paradigm: "Liq",
    summary:
      "H1112 와 같은 청산 캐스케이드 알파를 사용하되, 진입 조건을 더 느슨하게 하여 거래 빈도를 늘린 변형.",
    mechanism:
      "본질은 H1112 와 동일. z 임계치를 낮춰 거래 횟수를 늘리고 평균 수익은 작아지지만 통계 표본을 키우는 변형. 백테스트에서 lenient 가 IS/OOS 안정성이 더 높았다.",
    entryExit:
      "liq z>1.2 정도로 진입 문턱을 낮춤. TP/SL 폭도 좁게 조정.",
  },

  h1130: {
    name: "H1130 청산 모멘텀 변형",
    paradigm: "Liq",
    summary:
      "H1112 변형. 청산 발생 직후 가격이 같은 방향으로 잠시 모멘텀을 보일 때를 노린다.",
    mechanism:
      "청산이 폭증한 직후 짧은 시간(1~5분) 동안 같은 방향으로 추가 가격 이동이 일어나는 경우가 있다. H1112 가 역방향(역추세)이라면 H1130 은 순방향(추세 추종)으로 진입.",
    entryExit:
      "liq z 임계치 + 가격 모멘텀 동조 시 청산 방향으로 진입. 1시간 시간청산.",
  },

  h1132: {
    name: "H1132 청산 캐스케이드 변형",
    paradigm: "Liq",
    summary:
      "H1112 시리즈의 또 다른 변형. 진입 임계치/타임프레임을 다르게 한 직교 변형.",
    mechanism:
      "H1112/H1130 과 같은 청산 데이터를 사용하되 z 윈도우, 임계치, 청산 방향 필터 등을 달리하여 백테스트 통계가 가장 안정적이었던 조합.",
    entryExit:
      "기본은 H1112 와 동일하나 진입/청산 파라미터가 다르다.",
  },

  h1122: {
    name: "H1122 5분 거래량 폭증 + 신호결합 SHORT [폐기 2026-06-14]",
    paradigm: "Vol5m",
    summary:
      "[폐기] 5분 거래량이 평소 대비 폭증한 직후 진입하던 전략. 구현이 가설(평균회귀)과 정반대인 추세추종(momentum)으로 짜여 있었고, 백테스트 결과 음의 기대값(PF 0.70, 거래당 -0.084%)이었음. 페이퍼 +53K KRW 는 초기 소수코인 시절 XRP 대형 비중 거래의 가중 아티팩트일 뿐 실제 엣지가 아님. 방향을 바로잡은 교정판 H2020 으로 대체.",
    mechanism:
      "5분 캔들 거래량의 24시간(288바) z-score 가 임계치(z>=1.5) 이상이면 '단기 과열' 로 보고, 직전 1시간 가격 방향으로 진입했음(=추세추종). 문제: 메타 가설은 '폭증 후 평균회귀(fade)' 였는데 코드는 반대 방향이었음. 손절(-1%)이 익절(+1.8%)보다 약 6배 자주 터지는 구조라(SL 365건×-1% vs TP 58건×+1.5%) 백테스트 3/1~5/26(n=2654) PF 0.70. 페이퍼 거래당 평균수익률도 -0.0425% 로 이미 음수였음. 상세: BCR-117.",
    entryExit:
      "[폐기됨] (구) vol5m z>=1.5 + 직전 1h 가격부호 → momentum 진입, TP +1.8% / SL -1.0% / 60분. 방향 교정판 H2020(fade + tight 청산 + SHORT-only) 참조.",
  },

  h2020: {
    name: "H2020 5분 거래량 폭증 평균회귀 SHORT (h1122 교정판)",
    paradigm: "Vol5m fade",
    summary:
      "5분 거래량이 24시간 평균 대비 비정상적으로 폭증(z>=1.5)한 직후, 직전 1시간 동안 가격이 오른 종목을 '단기 과열·매수세 소진' 으로 보고 SHORT(공매도)로 평균회귀를 노리는 전략. 거래량 급증은 흔히 직후 되돌림을 동반한다는 점을 이용한다. 폐기된 h1122 가 방향을 반대(추세추종)로 구현해 손실이던 것을, 가설대로 fade(평균회귀)로 바로잡은 교정판이다.",
    mechanism:
      "거래량 z-score(z, 평소 대비 몇 표준편차 벗어났는지) = (현재 5분 거래량 − 288바 평균) ÷ 288바 표준편차. z>=1.5 면 '폭증' 으로 판정. h1122 는 이때 직전 추세를 따라갔지만(momentum), 백테스트로 그게 음의 기대값(PF 0.70 — Profit Factor=총이익/총손실, 1 미만이면 손실)임을 확인했다. 방향을 fade 로 뒤집자 두 독립 구간·14개 코인 전부 수익으로 반전(특정 코인 쏠림이 아니라 전체가 양수 = 진짜 신호). 하락을 fade(매수)하면 급락이 이어지는 '칼받기' 위험이 커, 상승 폭증만 SHORT 하는 단방향으로 비용 내성을 확보했다. ⚠️ TP 가 0.6% 로 좁아 비용에 민감 — 슬리피지 포함 8bps 이상이거나 시장가(taker)로 체결되면 수익이 사라진다 → 지정가(maker) 체결 전제.",
    entryExit:
      "진입: 5분 거래량 z>=1.5 + 직전 1시간 가격 상승 + BTC 1시간 변동성 regime(ATR 비율 0.9~2.0 — 너무 잔잔하거나 폭발적인 장 제외) → 해당 코인 SHORT. 청산: 부분익절 +0.4% 에서 33% 청산 후 손절선을 본전(BE)으로 이동 / 전량 익절 TP +0.6% / 손절 SL -0.6% / 30분 시간청산. 비용 MAKER 0bps 전제. 백테스트(maker): 3/1~5/26 WR 62.7% PF 1.21 / 5/27~6/14 OOS WR 83.8% PF 3.71, 14코인 전부 양수. ⚠️ tight TP 비용민감 + 2주 페이퍼 최종검증 중(~6/28).",
  },

  h2022: {
    name: "H2022 15분 거래량 폭증 평균회귀 양방향 (h1130 교정판)",
    paradigm: "Vol5m fade",
    summary:
      "H2020 과 같은 '거래량 폭증 후 평균회귀(fade)' 알파를 15분봉에서 적용한 양방향 버전. 폐기된 h1130(15분, 추세추종으로 잘못 구현되어 손실)의 방향을 바로잡은 교정판이다. 5분(H2020)보다 한 단계 긴 시간축이라 더 큰 흐름의 과열·소진을 잡고, 상승 폭증은 SHORT·하락 폭증은 LONG 양방향으로 되돌림을 노린다.",
    mechanism:
      "15분봉 거래량의 24시간(96바) z-score 가 z>=1.5 면 '폭증' 으로 판정하고, 직전 1시간 추세의 반대 방향으로 진입한다(fade). 폐기된 h1130 은 추세를 따라가(momentum) 음의 기대값(백테스트 PF 0.78 — Profit Factor, 1 미만은 손실)이었으나, 방향을 fade 로 뒤집자 두 독립 구간 모두 강하게 수익 전환. 15분 시간축은 5분보다 노이즈가 적고 비용 비중이 낮아, 5분(H2020)에서 비용 때문에 단방향(SHORT)만 쓰던 것과 달리 양방향 모두 안정적으로 작동한다. ⚠️ 같은 vol-fade 알파군이라 H2020/H2023 과 부분적으로 상관될 수 있음 → 분산은 시간축 다양화 수준.",
    entryExit:
      "진입: 15분 거래량 z>=1.5 + BTC 1시간 변동성 regime(ATR 0.9~2.0) → 직전 1시간 추세의 반대 방향(상승→SHORT, 하락→LONG). 청산: 부분익절 +0.7% 33%→본전(BE) / TP +1.2% / SL -1.0% / 90분 시간청산. 비용 MAKER 0bps. 백테스트(maker): 3/1~5/26 WR 67.3% PF 1.62 Sharpe 7.89(n=1665) / 5/27~6/14 OOS WR 75.0% PF 2.72 Sharpe 7.88(n=352). ⚠️ 2주 페이퍼 최종검증 중(~6/28).",
  },

  h2023: {
    name: "H2023 30분 거래량 폭증 평균회귀 양방향 (h1132 교정판)",
    paradigm: "Vol5m fade",
    summary:
      "H2020/H2022 와 같은 '거래량 폭증 후 평균회귀(fade)' 알파를 30분봉에서 적용한 양방향 버전. 폐기된 h1132(30분, 추세추종 오구현으로 손실)의 방향을 바로잡은 교정판이다. 세 전략 중 가장 긴 시간축으로, 더 큰 단위의 과열 되돌림을 잡는다.",
    mechanism:
      "30분봉 거래량의 24시간(48바) z-score 가 z>=1.5 면 '폭증', 직전 1시간 추세의 반대로 진입(fade). 폐기된 h1132 는 추세추종으로 음의 기대값(백테스트 PF 0.73)이었으나 fade 교정으로 두 독립 구간 모두 수익 전환. 30분 시간축은 비용 비중이 가장 낮아 양방향이 안정적이며 거래 빈도는 셋 중 가장 낮다. ⚠️ H2020(5분)/H2022(15분) 와 동일 알파군 — 시간축만 다른 분산.",
    entryExit:
      "진입: 30분 거래량 z>=1.5 + BTC 1시간 변동성 regime(ATR 0.9~2.0) → 직전 1시간 추세의 반대 방향. 청산: 부분익절 +1.0% 33%→본전(BE) / TP +1.8% / SL -1.5% / 180분 시간청산. 비용 MAKER 0bps. 백테스트(maker): 3/1~5/26 WR 62.6% PF 1.38 Sharpe 4.15(n=1074) / 5/27~6/14 OOS WR 76.1% PF 2.42 Sharpe 5.17(n=218). ⚠️ 2주 페이퍼 최종검증 중(~6/28).",
  },

  h2023t: {
    name: "H2023T 30분 거래량 폭증 평균회귀 양방향 — taker 정직비용 A/B",
    paradigm: "Vol5m fade",
    summary:
      "H2023과 완전히 동일한 신호·파라미터에 체결 방식만 바꾼 A/B(비교) 실험 전략. '지정가 대기 시 수수료 0원' 가정이 실전에서 성립하지 않는다는 실측을 확인한 뒤, 시장가(taker) 즉시 체결 + 정직한 비용(수수료 4bps+슬리피지 2bps)으로 h2023 원본의 성과가 실전에서도 유지되는지 검증하기 위해 배포됐다. 2026-08-04 판정에서 h2023 본체와 함께 은퇴 권고를 받았다.",
    mechanism:
      "신호는 H2023과 동일: 30분봉 거래량 z-score(24시간 평균 대비 표준편차 거리)>=1.5 + 직전 1시간 추세의 반대 방향(fade) 진입. 차이는 체결·비용 모델뿐 — 지정가 대기 없이 즉시 시장가로 체결시켜 paper 성과를 실전과 같은 척도로 맞춘다. A/B 목적(원본이 taker 비용에서도 살아남는가)은 28일 관측으로 사실상 결론이 났다: 트윈이 원본보다 우세했던 날은 28일 중 3일뿐이고, 원본(h2023) 자체도 청정 구간 PF 1.00으로 무엣지 판정.",
    entryExit:
      "H2023와 동일 (부분익절 +1.0% 33%→본전 / TP +1.8% / SL -1.5% / 180분 시간청산). 2026-08-04 함대 판정: 전이력 n=214, PF 0.70(record=taker, 태생부터 taker 비용 반영이라 손상 없음) — 배포 이후 누적 적자 지속으로 h2023 본체와 함께 은퇴 권고(처분 결정은 사용자 대기 중).",
  },

  h2113: {
    name: "H2113 5분 변동성 레인지 폭증 평균회귀 SHORT",
    paradigm: "Vol5m fade",
    summary:
      "5분봉의 고저 변동폭(레인지)이 24시간 평균 대비 비정상적으로 폭증(z>=2.0)한 직후, 직전 1시간 동안 오른 종목을 '변동성 climax·과열'로 보고 SHORT 평균회귀(fade) 진입. H2020 계열이 '거래량 폭증'을 쓰는 데 비해 이 전략은 '가격 변동폭 폭증'을 신호로 쓴다 — 거래량보다 비용 내성이 강한 것이 특징.",
    mechanism:
      "신호 = (고가-저가)/종가 의 24시간(288바) z-score. z>=2.0 면 변동성 climax 로 판정하고, 직전 1시간 상승분을 fade 하여 SHORT. 거래량 z(H2020) 대비 레인지 z 는 같은 fade 알파를 다른 신호원으로 포착하며, 백테스트상 거래 수수료(maker 4bps)에도 수익이 유지될 만큼 신호 품질이 높다(거래량판은 0bps 전제). ⚠️ H2020/H2022/H2023(거래량 fade)과 같은 'spike→fade' 알파군이라 상관이 높음 — 동시진입 캡 3으로 시장 동반 폭증 시 집중 위험을 제한.",
    entryExit:
      "진입: 5분 레인지 z>=2.0 + 직전 1시간 상승 + BTC 1시간 변동성 regime(ATR 0.9~2.0) → SHORT. 청산: 부분익절 +0.4% 33%→본전(BE) / TP +0.6% / SL -0.6% / 30분 시간청산. 동시 오픈 최대 3개(집중 캡). MAKER. 백테스트: 3/1~5/26 WR 70% PF 1.94 / 5/27~6/14 OOS WR 83% PF 4.48 (maker 4bps), 12~14/14코인 양수. ⚠️ backtest-GO≠live-GO → 2주 페이퍼 최종검증.",
  },

  h2115: {
    name: "H2115 15분 변동성 레인지 폭증 평균회귀 SHORT",
    paradigm: "Vol5m fade",
    summary:
      "H2113 과 같은 '변동성 레인지 폭증 후 평균회귀' 알파를 15분봉에서 적용한 SHORT 버전. 발굴한 spike-fade 계열 중 백테스트 성적이 가장 강했다(OOS PF 5.46, 전 코인 양수). 5분(H2113)보다 한 단계 긴 시간축으로 더 큰 변동성 climax 의 되돌림을 잡는다.",
    mechanism:
      "15분봉 (고가-저가)/종가 의 24시간(96바) z-score >= 2.0 → 변동성 climax. 직전 1시간 상승을 fade 하여 SHORT. 15분 시간축은 노이즈가 적고 비용 비중이 낮아 신호 품질이 가장 높다. ⚠️ H2113/H2020 계열과 동일 알파군(상관 높음) — 동시진입 캡 3.",
    entryExit:
      "진입: 15분 레인지 z>=2.0 + 직전 1시간 상승 + BTC regime(ATR 0.9~2.0) → SHORT. 청산: 부분익절 +0.7% 33%→본전(BE) / TP +1.2% / SL -1.0% / 90분 시간청산. 동시 오픈 최대 3개. MAKER. 백테스트: 3/1~5/26 WR 73% PF 1.93(14/14코인) / 5/27~6/14 OOS WR 89% PF 5.46(11/11). ⚠️ backtest-GO≠live-GO → 2주 페이퍼 최종검증.",
  },

  h2020t:{name:"H2020T 5분 거래량 폭증 fade SHORT — taker 정직비용 A/B",paradigm:"Vol5m fade",summary:"H2020과 완전히 동일한 신호·파라미터에 비용 모델만 taker 정직비용(수수료 4bps+슬리피지 2bps)으로 바꾼 A/B 실험 전략. 2026-07-12 함대점검 비용감사(BCR-129)에서 H2020/22/23의 진입 레그가 영구 MAKER-0(수수료 0원 가정)이고, taker 비용을 강제하면 3종 합산 -258만원 적자로 전환됨을 확인 → h2115t 선례대로 정직비용 트윈을 병행 관찰한다.",mechanism:"신호는 H2020과 동일(5분 거래량 z>=1.5 + 직전 1시간 상승 fade SHORT). 차이는 비용뿐 — 진입·청산 전 레그 taker 6bps(왕복 12bps). 체결가는 원본과 동일(관측 mark)이므로 순수 비용 상수 A/B이며, 30일 후 승자만 유지한다. TP 0.6%로 좁아 비용 민감도가 가장 큰 계열이라 이 검증이 GO/실전 전환의 전제조건이다.",entryExit:"H2020과 동일 (부분익절 +0.4%→BE / TP +0.6% / SL -0.6% / 30분 시간청산 / 동시 4슬롯)."},
  h2022t:{name:"H2022T 15분 거래량 폭증 fade 양방향 — taker 정직비용 A/B",paradigm:"Vol5m fade",summary:"H2022와 완전히 동일한 신호에 비용 모델만 taker 정직비용(4+2bps)으로 바꾼 A/B 실험 전략 (BCR-129 비용감사). 원본은 진입 레그 MAKER-0 가정이라 실전 체결이 시장가로 기울면 성과가 사라질 수 있어, 정직비용 트윈으로 30일 병행 검증한다.",mechanism:"신호는 H2022와 동일(15분 거래량 z>=1.5 + 직전 1시간 추세 반대 양방향 fade). 진입·청산 전 레그 taker 6bps 부과 — 체결가 동일, 비용 상수만 차이나는 순수 A/B.",entryExit:"H2022와 동일. 30일 A/B 후 승자만 유지."},
  h2115t:{name:"H2115T 15분 레인지 폭증 fade SHORT — taker 정직비용 A/B",paradigm:"Vol5m fade",summary:"H2115와 완전히 동일한 신호·파라미터에 체결 방식만 바꾼 A/B 실험 전략. 2026-07-03 real-test 패널이 'MAKER 수수료 0원' 가정이 실전에서 성립하지 않음(지정가 대기 중 불리한 거래만 체결되는 역선택)을 실측으로 확인 → taker 즉시 진입 + 정직한 비용(수수료 4bps+슬리피지 2bps)으로 재검증한다.",mechanism:"신호는 H2115와 동일: 15분봉 레인지 z>=2.0 + 직전 1시간 상승 fade SHORT. 차이는 체결·비용 모델뿐 — 진입·청산 전 레그에 taker 비용을 부과해 paper 성과가 실전과 같은 척도가 되도록 한다. 패널 재터치 시뮬레이션에서 patient-maker는 PF 0.94로 붕괴, taker는 PF 1.76으로 우월했던 결과의 라이브 검증.",entryExit:"H2115와 동일 (부분익절 +0.7%→BE / TP +1.2% / SL -1.0% / 90분 시간청산 / 동시 3슬롯). 30일 h2115 대비 A/B 후 승자만 유지."},h2116: {
    name: "H2116 5분 청산 폭증 평균회귀 SHORT",
    paradigm: "Vol5m fade",
    summary:
      "5분 동안 청산(강제 포지션 정리) 금액이 24시간 평균 대비 폭증한 직후, 직전 1시간 오른 종목을 '숏 스퀴즈 climax'로 보고 SHORT 평균회귀(fade) 진입. H2113/H2115(가격 변동폭)와 달리 '청산 금액'을 신호원으로 쓴다 — 거래량·변동폭과 가장 독립적인(직교) 데이터라 같은 fade 알파군 중 분산 효과가 가장 크다.",
    mechanism:
      "5분 청산 notional 합계의 24시간(288바) z-score >= 2.0 면 청산 climax(과도한 강제 매수=숏 스퀴즈)로 판정, 직전 1시간 상승을 fade 하여 SHORT. 청산 데이터는 거래량/변동폭과 발생 메커니즘이 달라(강제 청산 vs 자발적 거래) 같은 spike-fade 계열 중 가장 직교적. 백테스트(maker): TRAIN WR67% PF1.49 / OOS WR64% PF1.36, 10/11코인 양수. ⚠️ 라이브 청산 WS는 5코인(BTC/ETH/SOL/XRP/ADA)만 커버 → 5코인 한정 운영. 청산 버퍼가 24시간 채워진 뒤 발화(cold-start). h2020 계열과 동일 fade 알파군 — 동시진입 캡 3.",
    entryExit:
      "진입: 5분 청산 notional z>=2.0 + 직전 1시간 상승 + BTC regime(ATR 0.9~2.0) → SHORT (5코인). 청산: 부분익절 +0.4% 33%→본전(BE) / TP +0.6% / SL -0.6% / 30분 시간청산. 동시 오픈 최대 3개. MAKER. 백테스트: 3/1~5/1 WR67% PF1.49 / 5/1~6/3 OOS WR64% PF1.36. ⚠️ small-n·청산데이터 6/3까지 검증 + backtest-GO≠live-GO → 2주 페이퍼 최종검증.",
  },

  h2157: {
    name: "H2157 XRP앵커 15분 변동성 폭증 평균회귀 — 하드손절 제거",
    paradigm: "Spike fade (no-SL)",
    summary:
      "H2115 계열(15분 변동성 폭증 후 평균회귀)에서 '손절을 없앤' 관측용 전략. 분석 결과 tight 손절이 오히려 수익의 알파를 깎아먹는 '알파 살인자'로 지목되어, 하드손절 대신 시간청산(2시간)을 주력으로 삼는다. XRP를 양방향 앵커로, BTC/ETH/SOL은 상승 스파이크만 되파는(SHORT) 4코인 전용 — 추세를 잘 타는 알트코인(칼받기 위험)은 구조적으로 배제했다.",
    mechanism:
      "15분봉 (고가-저가)/종가 의 24시간(96바) z-score >= 2.0 이면 변동성 climax(급등락 절정)로 판정. 방향은 직전 1시간 움직임을 되파는 평균회귀 — XRP는 상승·하락 모두 fade(양방향 앵커, 심볼 고유 알파), BTC/ETH/SOL은 상승 스파이크만 fade(SHORT-bias, 하락 되사기는 칼받기라 배제). '하드손절이 알파를 죽인다'는 가설을 검증하려 tight SL 을 제거하고, 극단 방어용으로만 진입시점 15분 ATR(평균 진폭)×2.5 를 재난스톱으로 둔다. 부분익절 도달 후엔 스톱을 본전(BE)으로 옮겨 손실 꼬리를 막는다.",
    entryExit:
      "진입: 15분 레인지 z>=2.0 + BTC regime(ATR 0.9~2.0) → XRP 양방향 / BTC·ETH·SOL 상승만 SHORT. 다음 봉 진입(룩어헤드 금지). 청산: 부분익절 +0.7% 33%→본전(BE) / 하드TP·하드SL 없음 / 재난스톱 ATR×2.5 / 120분 시간청산. maker(지정가) flat 4bps round-trip. 동시 오픈 최대 4개. 재백테스트 관측배포 GO: OOS PF 1.49 / TAIL PF 1.96(maker). ⚠️ 관측 목적 — n>=30 도달 시 KILL 게이트, 증액 금지. backtest-GO≠live-GO.",
  },

  h1123: {
    name: "H1123 5분 변동성 + 신호 결합 LONG",
    paradigm: "Vol5m",
    summary:
      "H1122 의 반대 방향 변형. 변동성 폭증 + 보조 신호 결합 시 LONG.",
    mechanism:
      "H1122 와 동일한 vol5m 시그널을 사용하되, 시장 흐름이 상승 추세일 때는 변동성 폭증이 매수 기회로 작동하는 경우가 있어 LONG 진입.",
    entryExit:
      "vol5m z>2 + 상승 추세 보조신호 동시 만족 시 LONG.",
  },

  h1126: {
    name: "H1126 5분 변동성 + Confluence 변형",
    paradigm: "Vol5m",
    summary:
      "H1122/H1123 시리즈의 변형. 신호 결합(confluence) 조건을 다르게 잡았다.",
    mechanism:
      "vol5m 시그널에 추가로 가격 위치(VWAP 대비), 거래량, OI 등 다른 시그널 중 2개 이상이 동시에 성립할 때만 진입하는 confluence 강화 변형.",
    entryExit:
      "vol5m + confluence 2/3 이상 만족 시 진입.",
  },

  h1127: {
    name: "H1127 5분 변동성 결합 변형",
    paradigm: "Vol5m",
    summary:
      "H1126 의 또 다른 confluence 조합 변형.",
    mechanism:
      "보조 신호 조합을 다르게 한 변형. h1122~h1131 시리즈는 같은 vol5m 알파를 다양한 보조 신호와 결합한 직교 실험.",
    entryExit:
      "vol5m z + 보조신호 조합 만족 시 진입.",
  },

  h1131: {
    name: "H1131 Confluence 강화 변형",
    paradigm: "Vol5m",
    summary:
      "vol5m 시리즈에서 confluence 임계치를 가장 엄격하게 잡은 변형. 거래 빈도는 낮지만 정확도가 가장 높다.",
    mechanism:
      "vol5m + 보조신호 3개 이상 동시 만족 + 변동성 regime 필터까지 모두 통과해야 진입.",
    entryExit:
      "엄격 confluence 만족 시에만 진입.",
  },

  h1320: {
    name: "H1320 ETH 현선 베이시스 평균회귀 (15m)",
    paradigm: "Basis",
    summary:
      "이더리움 현물과 선물 가격 차이(베이시스)가 평소 대비 크게 벌어지면 곧 좁혀진다는 가정으로 진입.",
    mechanism:
      "Binance Spot ETH 가격과 Futures ETH 가격의 차이를 베이시스라 한다. 이 베이시스가 24시간 평균 대비 z-score 임계치 이상으로 크게 벗어나면 시장 효율성에 의해 곧 평균으로 돌아오는 경향을 활용. 15분 봉 기준.",
    entryExit:
      "베이시스 z>2 (선물 프리미엄 과대) → 선물 SHORT, z<-2 → 선물 LONG. TP +0.5% / SL -0.3% / 시간청산 4h.",
  },

  h1330: {
    name: "H1330 DOGE 현선 베이시스 평균회귀",
    paradigm: "Basis",
    summary:
      "DOGE 의 현선 베이시스 평균회귀 변형. ETH/AAVE 와 함께 paper 첫 실증 GO 후보.",
    mechanism:
      "H1320 과 동일 메커니즘. 다만 30+ 알트 중 ETH/DOGE/AAVE 3 코인만 베이시스 알파가 진성으로 작동했다는 발견이 핵심.",
    entryExit:
      "DOGE basis z 임계치 만족 시 선물 역방향 진입.",
  },

  h1341: {
    name: "H1341 AAVE 현선 베이시스 평균회귀",
    paradigm: "Basis",
    summary:
      "AAVE 베이시스 평균회귀. AAVE 는 5가지 베이시스 framework(15m/1h/spread/delta) 모두 작동하는 알파의 메카.",
    mechanism:
      "AAVE Spot/Futures 베이시스 z-score 평균회귀. 알트 중 가장 폭넓게 알파가 발견된 코인.",
    entryExit:
      "AAVE basis z 임계치 만족 시 역방향 진입.",
  },

  h1320b: {
    name: "H1320b 현선 베이시스 풀드 바스켓 (6코인)",
    paradigm: "Basis",
    summary:
      "ETH 단독 H1320을 멀티코인으로 다각화한 풀드(pooled) 단일 전략. 한 전략이 여러 코인을 동시에 운용해 분산 효과와 통계 표본을 확보한다. 2026-07-03 real-test 패널 실측으로 AWE·ENA(유동성/적자)·GAS·FF(슬리피지 15bps 반영 시 적자)를 제외해 10→6코인으로 축소.",
    mechanism:
      "VIRTUAL/0G/PUMP + SOL/DOGE/XRP 6코인 각각의 선물-현물 베이시스를 15분봉 z-score로 평균회귀 매매. 195코인 스윕에서 IS·OOS 양 구간 흑자였던 코인 + 함대점검(2026-07-02) 원장검증 SOL/DOGE/XRP를 선별 후 유동성 실측(2026-07-03)으로 재선별. 코인별 12.5M KRW, 코인당 1슬롯. 펀딩비 정산 반영·크로스전략 노출캡 적용(2026-07-03).",
    entryExit:
      "basis z>+2.5 → 선물 SHORT, z<-2.5 → 선물 LONG. TP +2.0% / SL 1.5% / 최대 보유 240분.",
  },

  h1320v2: {
    name: "H1320-v2 개선 베이시스 바스켓 (7코인, 코인별 1천만원)",
    paradigm: "Basis",
    summary:
      "전문가 패널 분석으로 H1320을 개선한 신규 별개 전략. 손실의 전부였던 '추세에 밟히는 어중간한 숏'을 제거하고 손절·익절 폭을 넓혀 적자를 흑자로 전환(백테스트 PF 0.95→1.82). ETH+h1320b 7코인(2026-07-03 패널: AWE·ENA·GAS·FF 제외)을 코인별 1천만원으로 독립 운영 테스트.",
    mechanism:
      "비대칭 진입 임계가 핵심: SHORT는 z≥+3.5(통계적 극단 괴리만)로 올려 추세에 밟히는 모더릿 숏을 차단하고, LONG은 z≤-2.5 유지. 손절은 1.5→2.0%(베이시스 과이탈 시 회귀확률↑라 회복 허용), 익절 2.0→3.0%. ETH 단독 검증(IS PF1.57/OOS2.34)을 7코인(함대점검 원장검증 SOL/DOGE/XRP 편입, 2026-07-03 유동성 실측 재선별)으로 forward 검증 중.",
    entryExit:
      "LONG z≤-2.5 / SHORT z≥+3.5. TP +3.0% / SL 2.0% / 최대 보유 240분 / 쿨다운 270분.",
  },

  h1320v3: {
    name: "H1320-v3 피드가드 베이시스 바스켓 (6코인)",
    paradigm: "Basis",
    summary:
      "2026-07-08 phantom basis 사고(현물 가격 피드가 40~70초 동결된 채 가짜 프리미엄을 만들어 5코인 동시 숏 전패, -134만) 대응으로 만든 신규 별개 전략. h1320b와 신호·청산·유니버스·예산이 완전히 동일하고, 피드 무결성/상관집중 가드 4종만 추가한 깨끗한 A/B (BCR-128).",
    mechanism:
      "가드 4종이 유일한 차이: ① 선물·현물 두 가격의 수신 시각이 10초 이상 어긋나면 신호 폐기(사고 원인 직접 차단) ② 베이시스 절대값 6bps 미만(왕복비용 12bps의 절반)이면 노이즈로 간주해 진입 금지 ③ 20분 안에 3개 이상 코인이 동시에 진입 신호면 개별 코인 문제가 아닌 시장 전체 이벤트로 판단해 전원 진입 금지(백테스트: 동시포화 페이드는 PF 0.65의 음의 엣지) ④ z-score 분모(표준편차)에 절대 하한 1.5bps를 둬 조용한 장에서 1~2bps 노이즈가 극단 신호로 둔갑하는 결함 보완.",
    entryExit:
      "basis z>+2.5 → 선물 SHORT, z<-2.5 → 선물 LONG. TP +2.0% / SL 1.5% / 최대 보유 240분 (h1320b 동일). 코인별 12.5M KRW.",
  },

  h1320v4: {
    name: "H1320v4 베이시스 밴드 바스켓 (SOL+ARB, 피드가드+실측비용 재설계)",
    paradigm: "Basis",
    summary:
      "김프(국내외 가격 프리미엄)와 같은 원리로, 한 코인의 현물과 선물 가격 차이(베이시스)가 평소보다 크게 벌어지면 곧 좁혀진다는 데 베팅하는 전략의 4번째 개정판. 2026-07-08 '유령 베이시스'(존재하지 않는 괴리를 진짜 신호로 오인) 피드 결함으로 손실을 낸 뒤 원인을 고치고(v3), 실제 슬리피지·펀딩비까지 반영한 정직한 비용 기준으로 살아남는 코인만 골라(SOL+ARB) 재구성했다.",
    mechanism:
      "h1320(ETH 단일)→h1320b(6코인 풀드)→h1320v2(7코인, 비대칭 진입임계) 계보를 잇는다. 2026-07-08 라이브에서 베이시스 계산에 쓰인 가격 피드가 순간적으로 어긋나며 실재하지 않는 괴리(phantom basis)를 진짜 신호로 오인해 진입한 손실 사건이 발생 → v3에서 피드 정합성을 검증하는 가드 4종(옵트인)을 추가해 재배포했다. v4는 한 단계 더 나아가 실측 슬리피지+실측 펀딩비+1분봉 청산까지 반영한 '정직비용' 기준으로 기존 7코인 유니버스를 전면 재평가해, SOL과 ARB만 생존(코인 간 상관 0.06으로 낮아 분산 효과 큼, 두 반기 모두 PF 2.0 이상 유지)했다는 크리틱 패널(2026-07-14~15) 결론을 반영했다.",
    entryExit:
      "베이시스 z(24시간 평균 대비 표준편차 거리) ≤-2.5면 선물 LONG, ≥+2.5면 선물 SHORT. 코인당 운용자본 1,250만원. TP +2.0% / SL -3.5%(v2의 -1.5~-2.0%보다 확대 — 43일 격자 테스트에서 SL을 넓힐수록 성과가 단조 개선됐음, 단 생존 유니버스 한정 결론) / 최대 보유 240분 / 재진입 쿨다운 270분 / v3 피드가드 4종 유지. forward 게이트: 30일 n≥40 & PF≥1.3 & 하락국면 1회 포함 → 통과 시 소액 파일럿. 2026-08-04 함대 판정 기준 n=14 / PF 1.74로 흑자 유지 중이나, 거래 페이스(1.08건/일)로는 08-20 게이트 시점 n≈31 예상돼 n≥40 미달 전망 — 최근 10건 PF 0.90으로 둔화 + 대부분 시간청산(TP 도달률 7.1%)으로 체결 프로파일이 백테스트와 다르게 나타나는 중이라 게이트 판정 시 추가 점검 필요.",
  },

  h1420: {
    name: "H1420 ETH-BTC 상대 스프레드",
    paradigm: "Cross-coin",
    summary:
      "ETH 와 BTC 의 가격 비율 변화가 평균에서 크게 벗어나면 다시 좁혀진다. ETH-BTC pair trade.",
    mechanism:
      "ETH/BTC 가격 비율의 z-score 가 임계치 이상이면 'ETH 가 BTC 대비 과매수/과매도' 상태로 판단. BTC 대비 ETH 의 상대 강도를 활용한 pair trade.",
    entryExit:
      "ETH/BTC z>2 → ETH SHORT + BTC LONG, z<-2 → 반대. delta-neutral.",
  },

  h1421: {
    name: "H1421 DOGE-BTC 상대 스프레드",
    paradigm: "Cross-coin",
    summary:
      "DOGE 와 BTC 의 가격 비율 pair trade. H1441(DOGE-BTC MAKER) 의 가벼운 변형.",
    mechanism:
      "DOGE/BTC z-score 가 임계치 이상이면 mean-revert. BTC reference 만 작동(ETH/AAVE reference 는 NO-GO 였음).",
    entryExit:
      "DOGE/BTC z 임계치 만족 시 pair trade 진입.",
  },

  h1422: {
    name: "H1422 AAVE-BTC 상대 스프레드",
    paradigm: "Cross-coin",
    summary:
      "AAVE 와 BTC 의 가격 비율 pair trade.",
    mechanism:
      "AAVE/BTC z-score pair trade. BTC 가 reference 일 때만 알파 발현.",
    entryExit:
      "AAVE/BTC z 임계치 만족 시 pair trade 진입.",
  },

  h1492: {
    name: "H1492 AAVE Basis 변화율(Δ) 평균회귀",
    paradigm: "Basis",
    summary:
      "베이시스 자체(level)가 아닌 변화율(delta)이 평균에서 벗어났을 때를 노린다. AAVE 에서 강하게 작동.",
    mechanism:
      "베이시스 level 이 천천히 추세를 형성할 때는 평균회귀가 잘 안 통하지만, 1분당 변화율(basis_delta)은 더 빠르게 평균회귀하는 경향. AAVE 에서 12/14 split 통과 — 강한 진성 알파.",
    entryExit:
      "AAVE basis_delta z>2 만족 시 진입. 15분 봉 기준.",
  },

  h1502: {
    name: "H1502 AAVE 1h MAKER (Sh +15.24)",
    paradigm: "Basis",
    summary:
      "AAVE 베이시스 + 1시간 봉 + MAKER(2bps 수수료) 진입. 백테스트 IS Sharpe +15.24, WR 78.6% 의 강력한 변형.",
    mechanism:
      "AAVE 베이시스 알파를 1h timeframe + MAKER 진입으로 비용을 최대한 낮춘 변형. MAKER 진입은 지정가 체결로 수수료를 4bps→2bps 로 절감하지만 미체결 risk 존재.",
    entryExit:
      "1h basis z 임계치 + 지정가 진입 시도. 미체결 시 다음 봉 재시도.",
  },

  h1530: {
    name: "H1530 ETH Basis Δ 1h MAKER",
    paradigm: "Basis",
    summary:
      "ETH 의 1시간 basis_delta + MAKER 진입. H1492 의 ETH 변형.",
    mechanism:
      "ETH basis_delta z-score 평균회귀를 1시간 봉 + MAKER 로 운영. small-n 이지만 IS Sh 매우 높음.",
    entryExit:
      "1h ETH basis_delta z 임계치 + MAKER 지정가 진입.",
  },

  h1541: {
    name: "H1541 ETH Δ 1h Variant (역대 최강)",
    paradigm: "Basis",
    summary:
      "H1543 ETH delta 1h MAKER HO Sharpe +16.64, PF 24.57 — 발굴 시리즈 역대 최강 통계. 단 small-n caveat 강함.",
    mechanism:
      "ETH basis_delta 의 또 다른 1h variant. 백테스트 통계는 압도적이나 trade 수가 작아(n≈10) overfit 의심도 함께 가진다. paper validation 필수.",
    entryExit:
      "1h ETH delta variant z 임계치 + 엄격한 confluence.",
  },

  h315: {
    name: "H315 Multi-TF Momentum Confluence (BTC/ETH)",
    paradigm: "Cross-TF momentum",
    summary:
      "BTC/ETH 의 1h+4h+1d 세 시간대 momentum z-score 가 동시에 같은 방향일 때만 진입. '세 시간대 의견이 모두 일치할 때만 신뢰' — multi-TF AND filter.",
    mechanism:
      "각 timeframe(1h/4h/1d) 별 168h 롤링 z-score 계산. |z_1h|>=1.2 AND |z_4h|>=1.0 AND |z_1d|>=0.8 동시 만족 + 세 부호 일치 시 그 방향으로 LONG/SHORT (momentum follow). H1200 메타 규칙 ②(2-feat AND > 1-feat)를 timeframe 차원에 적용.",
    entryExit:
      "TP +150bps / SL -100bps / MAX_HOLD 24h / cooldown 24h/coin. BN futures MAKER 2bps + slip 2bps. 백테스트 50일(2026-03-01~06-01): n=39, WR 84.6%, PF 7.16, +1.43M KRW on 100M — small-n caveat, paper 검증으로 확정.",
  },

  h315_sol: {
    name: "H315 Multi-TF Momentum (SOL)",
    paradigm: "Cross-TF momentum",
    summary:
      "H315 multi-TF momentum 알파를 SOL 에 적용한 per-coin 변형 (BCR-114). 본질은 h315 와 동일 — 1h/4h/1d 세 시간대 momentum 일치 시 진입.",
    mechanism:
      "h315 와 동일한 multi-TF AND 신호(|z_1h|>=1.2 ∧ |z_4h|>=1.0 ∧ |z_1d|>=0.8 + 부호 일치). momentum 알파가 BTC/ETH 외 6+ 코인에 일반화됨을 백테스트로 확인 → 비-청산/비-베이시스 유형 밸런스 위해 SOL 격상.",
    entryExit:
      "TP +150bps / SL -100bps / 24h hold / cooldown 24h. 백테스트 102일 SOL: n=24, WR 75%, PF 4.93. 주의: 추세장 의존 — 횡보장에선 무거래(dormant, 무손실).",
  },

  h315_ada: {
    name: "H315 Multi-TF Momentum (ADA)",
    paradigm: "Cross-TF momentum",
    summary:
      "H315 multi-TF momentum 의 ADA per-coin 변형 (BCR-114). 검증 코인 중 가장 강한 PF.",
    mechanism:
      "h315 와 동일한 multi-TF AND 신호. ADA 가 momentum 일반화 검증에서 최강(PF 8.81). 비-청산/비-베이시스 유형 밸런스 격상.",
    entryExit:
      "TP +150bps / SL -100bps / 24h hold / cooldown 24h. 백테스트 102일 ADA: n=28, WR 82%, PF 8.81. 추세장 의존(횡보장 dormant=무거래, 무손실).",
  },

  h315_aave: {
    name: "H315 Multi-TF Momentum (AAVE)",
    paradigm: "Cross-TF momentum",
    summary:
      "H315 multi-TF momentum 의 AAVE per-coin 변형 (BCR-114).",
    mechanism:
      "h315 와 동일한 multi-TF AND 신호. AAVE momentum 알파 검증(PF 5.27, n=29). 비-청산/비-베이시스 유형 밸런스 격상.",
    entryExit:
      "TP +150bps / SL -100bps / 24h hold / cooldown 24h. 백테스트 102일 AAVE: n=29, WR 76%, PF 5.27. 추세장 의존(횡보장 dormant=무거래, 무손실).",
  },

  h315_link: {
    name: "H315 Multi-TF Momentum (LINK)",
    paradigm: "Cross-TF momentum",
    summary:
      "H315 multi-TF momentum 의 LINK per-coin 변형 (BCR-114).",
    mechanism:
      "h315 와 동일한 multi-TF AND 신호. LINK momentum 알파 검증(PF 2.69, n=26). 비-청산/비-베이시스 유형 밸런스 격상.",
    entryExit:
      "TP +150bps / SL -100bps / 24h hold / cooldown 24h. 백테스트 102일 LINK: n=26, WR 65%, PF 2.69. 추세장 의존(횡보장 dormant=무거래, 무손실).",
  },

  h2013_lb48: {
    name: "H2013 Xsect Low-Vol Market-Neutral (14coin K=4, lb 48h)",
    paradigm: "Xsect low-vol market-neutral",
    summary:
      "유동성 메이저 14코인을 매일 변동성으로 랭킹해 저변동 4코인 롱 / 고변동 4코인 숏 — 시장 방향과 무관한 market-neutral 팩터 (BCR-114 신규 유형 1호).",
    mechanism:
      "low-volatility anomaly: 변동성이 낮은 자산이 위험조정수익에서 우위라는 현상(주식/암호화폐 공통). 48h 1h-수익률 표준편차로 14코인 랭킹, 저변동 K=4 equal-weight 롱 + 고변동 K=4 숏 = 베타 중립 — 추세장/횡보장 양쪽에서 거래 발생(momentum 의 dormant 약점 극복). 단 OKX 교차검증에서 4/24~5/18 구간 −6~−11% 손실 regime 확인 — all-weather 아님, 수 주 드로다운 구간 존재. 일루quid 미드캡(FIL/NEAR/APT/ATOM)은 OOS sign-flip 원인이라 유니버스에서 의도적 제외.",
    entryExit:
      "24h 주기 전 레그 리밸런스 (TP/SL 없음). MAKER 2bps + slip 2bps (레그당 8bps RT). 백테스트 102일: IS PF 1.68 / OOS 2.58 / HO 6.40, de-risk(10/12/14코인) 통과. caveat: OOS n=13 소표본 → paper 최종 검증.",
  },

  h2013_lb168: {
    name: "H2013 Xsect Low-Vol Market-Neutral (14coin K=4, lb 168h)",
    paradigm: "Xsect low-vol market-neutral",
    summary:
      "h2013_lb48 과 동일 메커니즘, 변동성 lookback 7일(168h) variant. 파라미터 전 범위(48~240h) 일관 작동이 robust 근거.",
    mechanism:
      "168h realized vol 랭킹 — 저변동 4롱 / 고변동 4숏 market-neutral. lookback 이 길수록 랭킹이 안정적 (turnover 감소).",
    entryExit:
      "24h 리밸런스. MAKER 2bps + slip 2bps. 백테스트 102일: IS PF 1.70 / OOS 2.98 / 전 구간 양수.",
  },

  h2013_lb240: {
    name: "H2013 Xsect Low-Vol Market-Neutral (14coin K=4, lb 240h)",
    paradigm: "Xsect low-vol market-neutral",
    summary:
      "h2013_lb48 과 동일 메커니즘, 변동성 lookback 10일(240h) variant — 가장 느린 canonical.",
    mechanism:
      "240h realized vol 랭킹 — 저변동 4롱 / 고변동 4숏 market-neutral.",
    entryExit:
      "24h 리밸런스. MAKER 2bps + slip 2bps. 백테스트 102일: OOS PF 2.5~3.0 (14코인 유니버스에서 최강 구간), 전 구간 양수.",
  },

  h318_bnb: {
    name: "H318 Basis_delta BNB",
    paradigm: "Basis delta",
    summary:
      "BNB spot-futures basis 변화율(delta) z-score 가 극단으로 갈 때 mean-revert. H1543 (AAVE 검증 GO) 패턴의 BNB 확장.",
    mechanism:
      "BNB spot - futures 가격 차이의 1h 변화율을 168h rolling z-score 로 계산. |z|>=2.5 이면 basis 변화 극단 → 평균 회귀 베팅. H1200 8번째 메타 규칙(basis_delta = 진성 alpha source)을 BNB 에 적용.",
    entryExit:
      "BNB basis_delta |z|>=2.5 진입, mean-revert 청산. 백테스트 30일(2026-04-15~05-15) BNB 분량 paper 검증 중.",
  },

  h318_sol: {
    name: "H318 Basis_delta SOL",
    paradigm: "Basis delta",
    summary:
      "SOL spot-futures basis 변화율(delta) z-score 가 극단으로 갈 때 mean-revert. H1543 (AAVE 검증 GO) 패턴의 SOL 확장.",
    mechanism:
      "SOL spot - futures 가격 차이의 1h 변화율을 168h rolling z-score 로 계산. |z|>=2.5 이면 basis 변화 극단 → 평균 회귀 베팅. H1200 8번째 메타 규칙(basis_delta = 진성 alpha source)을 SOL 에 적용.",
    entryExit:
      "SOL basis_delta |z|>=2.5 진입, mean-revert 청산. 백테스트 30일(2026-04-15~05-15) SOL 분량 paper 검증 중.",
  },

  h318_doge: {
    name: "H318 Basis_delta DOGE",
    paradigm: "Basis delta",
    summary:
      "DOGE spot-futures basis 변화율(delta) z-score 가 극단으로 갈 때 mean-revert. H1543 (AAVE 검증 GO) 패턴의 DOGE 확장.",
    mechanism:
      "DOGE spot - futures 가격 차이의 1h 변화율을 168h rolling z-score 로 계산. |z|>=2.5 이면 basis 변화 극단 → 평균 회귀 베팅. H1200 8번째 메타 규칙(basis_delta = 진성 alpha source)을 DOGE 에 적용.",
    entryExit:
      "DOGE basis_delta |z|>=2.5 진입, mean-revert 청산. 백테스트 30일(2026-04-15~05-15) DOGE 분량 paper 검증 중.",
  },

  h502: {
    name: "H502 Liq Cascade × Multi-TF Momentum Confluence",
    paradigm: "Liq momentum confluence",
    summary:
      "거래소 강제청산(liquidation) 폭증 + 1h/4h momentum 동시 같은 방향일 때만 진입. 단독 liq cascade(H1046)의 paper fail을 multi-TF confluence로 해결.",
    mechanism:
      "5min liquidation 거래대금이 24h 평균 대비 z>=+1.5σ 로 급증하면 '청산 캐스케이드' 발생. 추가로 1h(168h roll)+4h(42바 roll) 두 timeframe momentum 부호가 cascade 방향과 동일할 때만 진입. liq 단독 reversal 함정을 momentum AND gate 가 차단 (H1200 메타 ② '2-feat > 1-feat' 직접 실증). cooldown 2h/coin.",
    entryExit:
      "liq z>=+1.5 + |z_1h|≥0.3 ∧ |z_4h|≥0.3 + 세 부호 일치 → cascade 방향 follow. TP +90bps / SL -50bps / MAX_HOLD 30min. BN futures MAKER 2bps + slip 2bps. 50일 백테스트(2026-04-15~06-03) n=123, WR 61.8%, PF 3.73, +358 USD on 100M KRW (BTC 49 / ETH 42 / SOL 32).",
  },

  h553: {
    name: "H553 H315 × H502 Liq Dual Confluence + 1d Gate",
    paradigm: "Liq momentum confluence",
    summary:
      "H315 multi-TF momentum × H502 liq cascade 결합 + 1d momentum gate 추가. V22 dual confluence 정공법으로 H502 대비 false positive 추가 차단.",
    mechanism:
      "5min liquidation z(24h baseline) >= 1.3σ + 1h + 4h + 1d momentum 모두 같은 방향 동시 일치 시 진입. H502(V21 GO)에서 1d momentum gate 추가하여 단기 noise 추가 차단. BTC/ETH/SOL 3 universe. H502와 신호 overlap 가능 — 1주 dual 운영 후 결과 비교 결정.",
    entryExit:
      "liq z>=1.3 + 1h+4h+1d momentum 부호 동일 → 같은 방향 진입. TP +85bps / SL -50bps / MAX_HOLD 45min / cooldown 3h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 n=57, WR 73.7%, PF 5.83, +187K KRW on 100M (BTC23/ETH20/SOL14). n=57 caveat — H502 dual 비교 검증 중.",
  },

  h1674: {
    name: "H1674 H502 × Funding Regime Gate",
    paradigm: "Liq momentum × funding",
    summary:
      "H502 liq cascade × multi-TF momentum confluence + funding regime gate. funding 부호가 진입 방향과 일치할 때만 통과 — single funding NO-GO를 filter로 재활용.",
    mechanism:
      "H502 (liq z + 1h/4h momentum AND) trigger + funding rate 부호가 reversal 방향과 일치하면 통과. funding 자체는 단독 시 NO-GO지만, 다른 confluence trigger에 noise 필터 역할로 효과적. BTC/ETH/SOL universe.",
    entryExit:
      "H502 trigger + funding regime gate 통과 → 같은 방향 진입. TP/SL/MAX_HOLD/cooldown은 sim_h1674 코드 참조. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 n=9, WR 88.9%, PF 6.03, +18.7K KRW (100M). n=9 small-caveat — paper 검증 필수 (V19 H293 50일 폭락 사례 참고).",
  },

  h1701_doge: {
    name: "H1701 DOGE Basis_delta × Momentum (relaxed)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "DOGE spot-futures basis 변화율 z-score + multi-TF momentum confirmation. V24 H698 threshold 완화 진화 — n 1.8배 증가 + PF 유지.",
    mechanism:
      "DOGE futures - spot basis의 1h 변화율을 168h rolling z-score 계산. |z|>=1.2 (V24 H698 |z|>=1.5에서 완화) + 1h/4h momentum confirmation 통과 시 진입. H1200 메타 ⑥ (basis 진성 알파 DOGE/AAVE universe) 적용.",
    entryExit:
      "DOGE basis_delta |z|>=1.2 + 1h/4h momentum AND → 같은 방향 진입. TP/SL/MAX_HOLD/cooldown sim_h1701 참조. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 n=18(DOGE 부분), WR 66.7%+, PF 2.01, +65K KRW (100M).",
  },

  h1701_aave: {
    name: "H1701 AAVE Basis_delta × Momentum (relaxed)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "AAVE spot-futures basis 변화율 z-score + multi-TF momentum. V24 H698 진화형의 AAVE 변형.",
    mechanism:
      "AAVE futures - spot basis_delta 168h rolling z. |z|>=1.2 + 1h/4h momentum AND. H1543 AAVE 5 framework GO 패턴의 변형.",
    entryExit:
      "AAVE basis_delta |z|>=1.2 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1701 참조.",
  },

  h1706_doge: {
    name: "H1706 DOGE Basis_delta × Momentum (ETH-extended pool)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "DOGE spot-futures basis 변화율 z-score + multi-TF momentum. H1701(DOGE/AAVE) pool에 ETH 추가한 3코인 universe 확장형 — V26 진성 알파 cross-confirm.",
    mechanism:
      "DOGE futures - spot basis의 1h 변화율을 168h rolling z-score 계산. |z|>=1.2 + 1h/4h momentum AND gate(같은 부호 fade 방향). H1200 메타 ⑥ basis 진성 알파 3코인(DOGE/AAVE/ETH) pooled. ETH 추가로 n 1.9배 증가 + PF 유지.",
    entryExit:
      "DOGE basis_delta |z|>=1.2 + |z_1h|>=0.4 ∧ |z_4h|>=0.3 → fade 방향 진입. TP +75bps / SL -50bps / hold 4h / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 pool n=68, WR 60.3%, PF 1.60, +16 USD (3코인 합산).",
  },

  h1706_aave: {
    name: "H1706 AAVE Basis_delta × Momentum (ETH-extended pool)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "AAVE spot-futures basis 변화율 z-score + multi-TF momentum. H1701 pool 확장 3코인 universe(DOGE/AAVE/ETH)의 AAVE 변형.",
    mechanism:
      "AAVE futures - spot basis_delta 168h rolling z. |z|>=1.2 + 1h/4h momentum AND. H1543 AAVE 5 framework GO 패턴 + 3코인 pooled universe.",
    entryExit:
      "AAVE basis_delta |z|>=1.2 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1706 참조. 50일 pool n=68, PF 1.60.",
  },

  h1706_eth: {
    name: "H1706 ETH Basis_delta × Momentum (3-coin pool)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "ETH spot-futures basis 변화율 z-score + multi-TF momentum. H1320 paper GO 검증된 ETH basis 진성 알파의 3코인 pooled 변형.",
    mechanism:
      "ETH futures - spot basis_delta 168h rolling z. |z|>=1.2 + 1h/4h momentum AND. H1320 (ETH 1h basis paper GO) 패턴을 basis_delta 변환 + 3코인 universe(DOGE/AAVE/ETH) pooled.",
    entryExit:
      "ETH basis_delta |z|>=1.2 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1706 참조. 50일 pool n=68, WR 60.3%, PF 1.60.",
  },

  h1707_doge: {
    name: "H1707 DOGE Basis_delta × Momentum (ultra-relaxed z>=1.0)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "DOGE spot-futures basis 변화율 z-score + multi-TF momentum. H1701 threshold 추가 완화(z 1.2 → 1.0) — V25 학습 '완화>확장' 직접 검증.",
    mechanism:
      "DOGE futures - spot basis_delta 168h rolling z. |z|>=1.0 (H1701 1.2 → 1.0 ultra-relaxed) + 1h/4h momentum 완화 gate (|z_1h|>=0.3 ∧ |z_4h|>=0.2). H1200 메타 ⑥ basis 진성 알파 + threshold spectrum 끝단 검증.",
    entryExit:
      "DOGE basis_delta |z|>=1.0 + |z_1h|>=0.3 ∧ |z_4h|>=0.2 → fade 진입. TP +75bps / SL -50bps / hold 4h / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 pool n=58, WR 58.6%, PF 1.58, +20.36 USD (V26 최대 절대 수익).",
  },

  h1707_aave: {
    name: "H1707 AAVE Basis_delta × Momentum (ultra-relaxed z>=1.0)",
    paradigm: "Basis delta + momentum confluence",
    summary:
      "AAVE spot-futures basis 변화율 z-score + multi-TF momentum. H1701 threshold 추가 완화(z 1.2 → 1.0).",
    mechanism:
      "AAVE futures - spot basis_delta 168h rolling z. |z|>=1.0 + 1h/4h momentum (|z_1h|>=0.3 ∧ |z_4h|>=0.2). H1543 AAVE GO + threshold ultra-relaxed.",
    entryExit:
      "AAVE basis_delta |z|>=1.0 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1707 참조. 50일 pool n=58, PF 1.58.",
  },

  h1708_doge: {
    name: "H1708 DOGE Basis LEVEL z fade + Momentum",
    paradigm: "Basis level + momentum confluence",
    summary:
      "DOGE spot-futures basis 절대값(level) z-score fade + multi-TF momentum. H1706/H1707의 basis_delta(변화율)와 직교한 alpha source — basis level 그 자체.",
    mechanism:
      "DOGE futures - spot basis 자체의 168h rolling z. |z|>=1.5 + 1h/4h momentum AND (|z_1h|>=0.4 ∧ |z_4h|>=0.3). H1330 (DOGE basis paper GO) 패턴 통합. H1200 메타 ⑧ 'basis level과 delta 직교 alpha' 직접 검증.",
    entryExit:
      "DOGE basis_z |z|>=1.5 + momentum AND → fade 진입(basis 급등 시 SHORT, 급락 시 LONG). TP +75bps / SL -50bps / hold 4h / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 pool n=40, WR 57.5%, PF 1.90, +10.81 USD (V26 최고 PF).",
  },

  h1708_aave: {
    name: "H1708 AAVE Basis LEVEL z fade + Momentum",
    paradigm: "Basis level + momentum confluence",
    summary:
      "AAVE spot-futures basis 절대값 z-score fade + multi-TF momentum. H1341 (AAVE basis paper GO) 패턴의 3코인 통합 변형.",
    mechanism:
      "AAVE futures - spot basis 168h rolling z. |z|>=1.5 + 1h/4h momentum AND. basis level alpha (delta와 직교).",
    entryExit:
      "AAVE basis_z |z|>=1.5 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1708 참조. 50일 pool n=40, PF 1.90.",
  },

  h1708_eth: {
    name: "H1708 ETH Basis LEVEL z fade + Momentum",
    paradigm: "Basis level + momentum confluence",
    summary:
      "ETH spot-futures basis 절대값 z-score fade + multi-TF momentum. H1320 (ETH basis paper GO) 패턴의 3코인 통합 변형.",
    mechanism:
      "ETH futures - spot basis 168h rolling z. |z|>=1.5 + 1h/4h momentum AND. H1320 paper GO 메커니즘 + level z fade.",
    entryExit:
      "ETH basis_z |z|>=1.5 + momentum AND → 진입. TP/SL/MAX_HOLD/cooldown sim_h1708 참조. 50일 pool n=40, WR 57.5%, PF 1.90.",
  },

  h800_doge: {
    name: "H800 DOGE Cross-coin Basis_delta Divergence",
    paradigm: "Cross-coin divergence",
    summary:
      "DOGE basis_delta z-score 와 다른 2 코인(AAVE/ETH) basis_delta z 평균 사이의 divergence fade. V27 신규 paradigm — 단일 z fade 와 직교한 cross-section alpha source.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인의 spot-futures basis 변화율(basis_delta)을 168h rolling z-score 계산. divergence = DOGE z - mean(AAVE z, ETH z) 임계치 |z|>=1.8 만족 시 DOGE 만 fade (DOGE 만 튀어올랐다 가정). 1h/4h momentum AND gate(|z_1h|>=0.4 ∧ |z_4h|>=0.3) 통과 시 진입. cross-section perspective 가 단일 코인 noise 를 필터.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +75bps / SL -50bps / hold 4h / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 n=40(3코인 합산), WR 57.5%, PF 1.59, +105 USD on 100M KRW. 30일 n=27 PF 1.92. sim_h800 코드 참조.",
  },

  h800_aave: {
    name: "H800 AAVE Cross-coin Basis_delta Divergence",
    paradigm: "Cross-coin divergence",
    summary:
      "AAVE basis_delta z 와 다른 2 코인(DOGE/ETH) basis_delta z 평균 사이의 divergence fade. V27 신규 paradigm — cross-section alpha source.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = AAVE z - mean(DOGE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 AAVE fade 진입. AAVE 5 framework(H1502/H1530/H1543) GO 패턴 + cross-section 변형.",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP/SL/MAX_HOLD/cooldown sim_h800 참조. 50일 pool n=40, PF 1.59.",
  },

  h800_eth: {
    name: "H800 ETH Cross-coin Basis_delta Divergence",
    paradigm: "Cross-coin divergence",
    summary:
      "ETH basis_delta z 와 다른 2 코인(DOGE/AAVE) basis_delta z 평균 사이의 divergence fade. V27 신규 paradigm — cross-section alpha source.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = ETH z - mean(DOGE z, AAVE z) 임계치 |z|>=1.8 + 1h/4h momentum AND. H1320 (ETH basis paper GO) 패턴의 cross-section 변형.",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. TP/SL/MAX_HOLD/cooldown sim_h800 참조. 50일 pool n=40, WR 57.5%, PF 1.59.",
  },

  h902_doge: {
    name: "H902 DOGE-BTC Spread Cross-coin Divergence",
    paradigm: "Cross-coin spread divergence",
    summary:
      "DOGE-BTC 가격 비율의 z-score 와 다른 코인(ETH-BTC/AAVE-BTC) 같은 비율 z 평균 사이 divergence. H1200 메타 ⑦ (spread vs BTC orthogonal alpha) 의 V28 cross-section 확장.",
    mechanism:
      "DOGE/ETH/AAVE 각 alt 의 spread = alt_price / BTC_price (futures close, 1h) 168h rolling z. divergence = DOGE z - mean(ETH z, AAVE z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 DOGE fade 진입. H800 (basis_delta) 과 직교 — basis 가 아닌 상대 가격 비율의 cross-section.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +75bps / SL -50bps / hold 4h / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 n=54(3코인 합산), WR 72.2%, PF 3.05, net positive. 30일 n=21 WR 76.2% PF 2.86. sim_h902 코드 참조.",
  },

  h902_aave: {
    name: "H902 AAVE-BTC Spread Cross-coin Divergence",
    paradigm: "Cross-coin spread divergence",
    summary:
      "AAVE-BTC 가격 비율의 z-score 와 다른 코인(ETH-BTC/DOGE-BTC) 같은 비율 z 평균 사이 divergence. V28 cross-section axis 3/5.",
    mechanism:
      "DOGE/ETH/AAVE 각 alt 의 spread = alt_price / BTC_price 168h rolling z. divergence = AAVE z - mean(ETH z, DOGE z) 임계치 |z|>=1.8 + 1h/4h momentum AND. AAVE 5 framework (H1502/H1530/H1543) GO 패턴 + cross-section vs BTC 변형.",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP/SL/MAX_HOLD/cooldown sim_h902 참조. 50일 pool n=54, PF 3.05 (3코인 합산).",
  },

  h902_eth: {
    name: "H902 ETH-BTC Spread Cross-coin Divergence",
    paradigm: "Cross-coin spread divergence",
    summary:
      "ETH-BTC 가격 비율의 z-score 와 다른 코인(AAVE-BTC/DOGE-BTC) 같은 비율 z 평균 사이 divergence. H1200 메타 ⑦ (spread vs BTC orthogonal alpha) 확장.",
    mechanism:
      "ETH/BTC 가격 비율 168h rolling z + AAVE/BTC, DOGE/BTC 같은 방식 z. divergence = ETH z - mean(AAVE z, DOGE z) 임계치 만족 시 진입. cross-section perspective 로 spread 변화의 noise 필터. H1320 (ETH basis paper GO) 직교 alpha.",
    entryExit:
      "ETH-BTC spread divergence 임계치 → fade 진입. 백테스트 50일 n=54 합계, WR 72.2%, PF 3.05. sim_h902 코드 참조.",
  },

  h1712_btc: {
    name: "H1712 BTC Cross-coin Liq Divergence",
    paradigm: "Liq cross-coin divergence",
    summary:
      "BTC 단독 강제청산 cascade + 다른 알트(ETH/SOL) 안정 상태인 divergence 발생 시 BTC reverse fade. V30 axis 3/5 신규 paradigm — V19 단일 코인 liq momentum(H1046/H1048) paper NO-GO 후 cross-section 변형으로 첫 GO.",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ETH z <= 0.5 AND SOL z <= 0.5. signed liq (long-side - short-side) > 0 → SHORT (over-react fade), < 0 → LONG. H800/H902 cross-section 검증 후 liq paradigm 재시도 — single NO-GO mechanism cross-section reframing.",
    entryExit:
      "BTC HOT + alts COOL → BTC reverse 진입. TP +60bps / SL -40bps / hold 60min / cooldown 4h. BN futures MAKER 2bps + slip 2bps (= 8bps RT). 백테스트 50일 pool n=74, WR 59.5%, PF 2.45, +217K KRW on 100M KRW (BTC/ETH/SOL 합산). 30일 n=53 PF 2.07 +129K KRW. sim_h1712 참조.",
  },

  h1712_eth: {
    name: "H1712 ETH Cross-coin Liq Divergence",
    paradigm: "Liq cross-coin divergence",
    summary:
      "ETH 단독 강제청산 cascade + BTC 안정 상태인 divergence 발생 시 ETH reverse fade. V30 axis 3/5.",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG (over-react fade). cross-section: alt 단독 over-liq → reverse expectation.",
    entryExit:
      "BTC COOL + ETH HOT → ETH reverse 진입. TP +60bps / SL -40bps / hold 60min / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 pool n=74 합산 PF 2.45 WR 59.5% +217K KRW (3코인 합산 통계). sim_h1712 참조.",
  },

  h1712_sol: {
    name: "H1712 SOL Cross-coin Liq Divergence",
    paradigm: "Liq cross-coin divergence",
    summary:
      "SOL 단독 강제청산 cascade + BTC 안정 상태인 divergence 발생 시 SOL reverse fade. V30 axis 3/5.",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. cross-section liq divergence — SOL 단독 panic 시 reverse fade.",
    entryExit:
      "BTC COOL + SOL HOT → SOL reverse 진입. TP +60bps / SL -40bps / hold 60min / cooldown 4h. BN futures MAKER 2bps + slip 2bps. 백테스트 50일 pool n=74 합산 PF 2.45 WR 59.5% +217K KRW (3코인 합산 통계). sim_h1712 참조.",
  },

  h1881_doge: {
    name: "H1881 DOGE Cross-coin Basis_delta Divergence (1h + Rebate)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate)",
    summary:
      "H800 (V27 cross-coin basis_delta divergence 1h paper GO) 의 fee tier 변형 — TP/SL/hold/cooldown 가속 + MAKER REBATE -1bps tier 적용. V44 axis 2/5 GO. H1843/H1870 (cross-coin liq + rebate) 와 완전 직교 paradigm (basis_delta cross-section).",
    mechanism:
      "DOGE/AAVE/ETH 각 코인의 spot-futures basis 변화율(basis_delta)을 168h rolling z-score 계산. divergence = DOGE z - mean(AAVE z, ETH z) 임계치 |z|>=1.8 만족 시 DOGE 만 fade. 1h/4h momentum AND gate(|z_1h|>=0.4 ∧ |z_4h|>=0.3) 통과 시 진입. cross-section perspective 가 단일 코인 noise 를 필터.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +60bps / SL -40bps / hold 60min / cooldown 2h. BN futures MAKER REBATE -1bps + slip 2bps (round-trip 2bps, BN VIP1+ tier 가정). 백테스트 50일 n=45 (3코인 합산), WR 57.8%, PF 2.45, +217K KRW on 100M KRW. 30일 n=27, WR 59.3%, PF 2.53 (30d→50d most stable axis). sim_h1881 참조.",
  },

  h1881_aave: {
    name: "H1881 AAVE Cross-coin Basis_delta Divergence (1h + Rebate)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate)",
    summary:
      "H800 AAVE 의 fee tier 변형 — TP/SL/hold/cooldown 가속 + MAKER REBATE -1bps tier 적용. V44 axis 2/5 GO. AAVE 5 framework (H1502/H1530/H1543) GO 패턴 + cross-section + rebate variant.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = AAVE z - mean(DOGE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 AAVE fade 진입. H800 동일 mechanism + rebate fee tier.",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP +60bps / SL -40bps / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=45, WR 57.8%, PF 2.45. sim_h1881 참조.",
  },

  h1881_eth: {
    name: "H1881 ETH Cross-coin Basis_delta Divergence (1h + Rebate)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate)",
    summary:
      "H800 ETH 의 fee tier 변형 — TP/SL/hold/cooldown 가속 + MAKER REBATE -1bps tier 적용. V44 axis 2/5 GO. H1320 (ETH basis paper GO) + cross-section + rebate variant.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = ETH z - mean(DOGE z, AAVE z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 ETH fade 진입. H800 동일 mechanism + rebate fee tier.",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. TP +60bps / SL -40bps / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=45, WR 57.8%, PF 2.45. sim_h1881 참조.",
  },

  h1902_doge: {
    name: "H1902 DOGE Basis_delta Divergence Dynamic Sizing (1h + Rebate)",
    paradigm: "Basis_delta dynamic sizing (1h + Rebate)",
    summary:
      "H1881 (V44 cross-coin basis_delta divergence + rebate) base signal 그대로 + **dynamic position sizing (z magnitude weighted)** 적용. V46 axis 3/5 GO. 강한 신호 시 position size 1×~2× scale 로 PF 향상 (2.45 → 2.52, 30d PF 2.53 → 3.75 큰 winner capture). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = DOGE z - mean(AAVE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 DOGE fade. Dynamic sizing: sizeMultiplier = clip((|divergence_z| - 1.5) / 0.5, 1.0, 2.0). |z|=1.8→1.0× / |z|=2.0→1.0× / |z|=2.3→1.6× / |z|=2.5→2.0× (cap). notionalKRW = base_notionalKRW × sizeMultiplier (base 5% → max 10%).",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +60bps / SL -40bps / hold 60min / cooldown 2h. BN futures MAKER REBATE -1bps + slip 2bps (round-trip 2bps, BN VIP1+ tier 가정). 백테스트 50일 n=45 (3코인 합산), WR 57.8%, PF 2.52, +228K KRW on 100M KRW (avg PnL +3.67 USD). 30일 n=27, WR 59.3%, PF 3.75 (큰 winner capture). 진입일 2026-06-08. sim_h1902_h1881_dynamic_sizing 참조.",
  },

  h1902_aave: {
    name: "H1902 AAVE Basis_delta Divergence Dynamic Sizing (1h + Rebate)",
    paradigm: "Basis_delta dynamic sizing (1h + Rebate)",
    summary:
      "H1881 AAVE base signal + dynamic position sizing (z magnitude weighted). V46 axis 3/5 GO. AAVE 5 framework (H1502/H1530/H1543) GO 패턴 + cross-section + dynamic sizing variant. 강한 신호 시 position 1×~2× scale.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = AAVE z - mean(DOGE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 AAVE fade. Dynamic sizing: sizeMultiplier = clip((|divergence_z| - 1.5) / 0.5, 1.0, 2.0). |z|=2.3→1.6× / |z|=2.5→2.0× (cap). notionalKRW = base × sizeMultiplier.",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP +60bps / SL -40bps / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=45, WR 57.8%, PF 2.52, +228K KRW. 30일 PF 3.75. 진입일 2026-06-08. sim_h1902_h1881_dynamic_sizing 참조.",
  },

  h1902_eth: {
    name: "H1902 ETH Basis_delta Divergence Dynamic Sizing (1h + Rebate)",
    paradigm: "Basis_delta dynamic sizing (1h + Rebate)",
    summary:
      "H1881 ETH base signal + dynamic position sizing (z magnitude weighted). V46 axis 3/5 GO. H1320 (ETH basis paper GO) + cross-section + dynamic sizing variant. 강한 신호 시 position 1×~2× scale.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = ETH z - mean(DOGE z, AAVE z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 ETH fade. Dynamic sizing: sizeMultiplier = clip((|divergence_z| - 1.5) / 0.5, 1.0, 2.0). |z|=2.3→1.6× / |z|=2.5→2.0× (cap). notionalKRW = base × sizeMultiplier.",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. TP +60bps / SL -40bps / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=45, WR 57.8%, PF 2.52, +228K KRW. 30일 PF 3.75. 진입일 2026-06-08. sim_h1902_h1881_dynamic_sizing 참조.",
  },

  h1911_btc: {
    name: "H1911 BTC Cross-coin Liq Divergence 15min HF + Dynamic Sizing + Rebate",
    paradigm: "Liq cross-coin div + dynamic sizing (HF + Rebate)",
    summary:
      "H1843 (V41 cross-coin liq divergence 15min HF + rebate) base signal 그대로 + **dynamic position sizing (target liq z magnitude weighted)** 적용. V47 axis 2/5 GO. H1902 (basis_delta paradigm) 의 dynamic sizing mechanism 을 H1911 (liq paradigm) 에 transfer 검증 성공 — **dynamic sizing 이 paradigm-agnostic mechanism 임을 입증**. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ETH z <= 0.5 AND SOL z <= 0.5 → BTC reverse. signed liq (long-side - short-side) > 0 → SHORT, < 0 → LONG. Dynamic sizing: sizeMultiplier = clip((target_z - 1.2) / 0.5, 1.0, 2.0). target_z=1.5→1.0× / target_z=1.7→1.0× / target_z=2.2→2.0× (cap). notionalKRW = base_notionalKRW × sizeMultiplier (base 5% → max 10%).",
    entryExit:
      "BTC HOT(z≥1.5) + alts COOL(z≤0.5) → BTC reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps, rebate 수익 가능). 백테스트 50일 n=87, WR 54.0%, PF 2.16, +0.28% 수익률 on 100M KRW (BTC/ETH/SOL 합산). 30일 n=65, WR 52.3%, PF 2.02. 진입일 2026-06-08. sim_h1911_h1843_dynamic_sizing 참조.",
  },

  h1911_eth: {
    name: "H1911 ETH Cross-coin Liq Divergence 15min HF + Dynamic Sizing + Rebate",
    paradigm: "Liq cross-coin div + dynamic sizing (HF + Rebate)",
    summary:
      "H1843 ETH base signal + dynamic position sizing (target liq z magnitude weighted). V47 axis 2/5 GO. H1902 (basis_delta) dynamic sizing 이 liq paradigm 으로 transfer 성공 — paradigm-agnostic mechanism 입증. case 2: BTC quiet + ETH hot → ETH reverse.",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z. case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5 → ETH reverse. signed liq 방향 reverse. Dynamic sizing: sizeMultiplier = clip((target_z - 1.2) / 0.5, 1.0, 2.0). target_z=1.7→1.0× / target_z=2.2→2.0× (cap). notionalKRW = base × sizeMultiplier (base 5% → max 10%).",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps (BN VIP1+ tier 가정). 50일 n=87, WR 54.0%, PF 2.16, +0.28% (BTC/ETH/SOL 합산). 진입일 2026-06-08. sim_h1911_h1843_dynamic_sizing 참조.",
  },

  h1911_sol: {
    name: "H1911 SOL Cross-coin Liq Divergence 15min HF + Dynamic Sizing + Rebate",
    paradigm: "Liq cross-coin div + dynamic sizing (HF + Rebate)",
    summary:
      "H1843 SOL base signal + dynamic position sizing (target liq z magnitude weighted). V47 axis 2/5 GO. case 2: BTC quiet + SOL hot → SOL reverse. dynamic sizing transfer mechanism (H1902) 적용.",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z. case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5 → SOL reverse. signed liq 방향 reverse. Dynamic sizing: sizeMultiplier = clip((target_z - 1.2) / 0.5, 1.0, 2.0). target_z=1.7→1.0× / target_z=2.2→2.0× (cap). notionalKRW = base × sizeMultiplier.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps (BN VIP1+ tier 가정). 50일 n=87, WR 54.0%, PF 2.16, +0.28% (BTC/ETH/SOL 합산). 진입일 2026-06-08. sim_h1911_h1843_dynamic_sizing 참조.",
  },

  h1921_btc: {
    name: "H1921 BTC Cross-coin Liq Divergence 15min HF + Smart Breakeven Trail + Rebate",
    paradigm: "Liq cross-coin div + smart trail (HF + Rebate)",
    summary:
      "H1843 (V41 cross-coin liq divergence 15min HF + rebate) base signal 그대로 + **smart breakeven trail stop** (NEW exit-side lever) 적용. V48 axis 2/5 GO — V31-V48 paper 후보 중 최고 PF (50일 PF 2.47). 진입 후 +20bps 도달 시 SL 을 +0bps (breakeven) 로 이동 → winner preservation + downside cut. **Exit-side levers (trail/decay/lock) 이 paradigm-agnostic transfer 임을 입증** (H1911 dynamic sizing transfer 이어 두번째). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ETH z <= 0.5 AND SOL z <= 0.5 → BTC reverse. signed liq (long-side - short-side) > 0 → SHORT, < 0 → LONG. **Smart breakeven trail**: 진입 후 unrealized PnL (mark 기준) >= +20bps 도달 시 SL → entry +0bps (breakeven) 로 이동. 단방향 trail (한번 발동 후 추가 이동 X). hit_breakeven flag 영속화로 재시작 후 중복 방지.",
    entryExit:
      "BTC HOT(z≥1.5) + alts COOL(z≤0.5) → BTC reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps (breakeven) trail / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps, rebate 수익 가능). 백테스트 50일 n=87, WR 50.6%, PF **2.47** (V31-V48 최고). 30일 n=65, WR 47.7%, PF 2.38 (30d→50d 안정 + 개선). 진입일 2026-06-08. sim_h1921_h1843_smart_trail_stop 참조.",
  },

  h1921_eth: {
    name: "H1921 ETH Cross-coin Liq Divergence 15min HF + Smart Breakeven Trail + Rebate",
    paradigm: "Liq cross-coin div + smart trail (HF + Rebate)",
    summary:
      "H1843 ETH base signal + smart breakeven trail stop (NEW exit-side lever). V48 axis 2/5 GO. case 2: BTC quiet + ETH hot → ETH reverse. exit-side lever transfer mechanism — winner preservation 강화. V31-V48 paper 후보 중 최고 PF (50일 2.47).",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z. case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5 → ETH reverse. signed liq 방향 reverse. Smart trail: 진입 후 unrealized PnL >= +20bps 도달 시 SL → entry (breakeven) 이동. 단방향 (1회만 발동).",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps (BN VIP1+ tier 가정). 50일 n=87, WR 50.6%, PF 2.47 (BTC/ETH/SOL 합산, V31-V48 최고). 진입일 2026-06-08. sim_h1921_h1843_smart_trail_stop 참조.",
  },

  h1921_sol: {
    name: "H1921 SOL Cross-coin Liq Divergence 15min HF + Smart Breakeven Trail + Rebate",
    paradigm: "Liq cross-coin div + smart trail (HF + Rebate)",
    summary:
      "H1843 SOL base signal + smart breakeven trail stop. V48 axis 2/5 GO. case 2: BTC quiet + SOL hot → SOL reverse. exit-side lever (trail) paradigm-agnostic 입증. V31-V48 paper 후보 중 최고 PF (50일 2.47).",
    mechanism:
      "H1843 signal 동일 — BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z. case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5 → SOL reverse. signed liq 방향 reverse. Smart trail: 진입 후 unrealized PnL (mark 기준) >= +20bps 도달 시 SL → entry (breakeven) 이동. 단방향 trail.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps (BN VIP1+ tier 가정). 50일 n=87, WR 50.6%, PF 2.47 (BTC/ETH/SOL 합산, V31-V48 최고). 진입일 2026-06-08. sim_h1921_h1843_smart_trail_stop 참조.",
  },

  h1933_doge: {
    name: "H1933 DOGE Cross-coin Basis_delta Divergence 1h + Smart Breakeven Trail + Rebate",
    paradigm: "Basis_delta cross-coin div + smart trail (1h + Rebate)",
    summary:
      "H1881 (V44 cross-coin basis_delta divergence 1h + rebate) base signal 그대로 + **smart breakeven trail stop** 적용. V49 axis 4/5 GO. **NEW transfer 입증** — smart trail lever 가 V48 H1921 (liq paradigm GO) 에서 V49 H1933 (basis paradigm GO) 으로 전이. exit-side levers (trail/decay/lock) paradigm-agnostic 재확인. 진입 후 +25bps 도달 시 SL → +0bps (breakeven) 이동. H1881 PF 2.45 → H1933 PF 2.60 (+0.15 향상). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH 각 코인 basis_bps = (futures - spot) / spot × 10000. basis_delta_t = basis_t - basis_(t-1). 168h(7d) rolling z-score on delta (코인별 독립). divergence_z = z[target] - mean(z[others]). |divergence_z| >= 1.8 → target only fade. Multi-TF momentum AND gate: |z_1h (168h rolling ret)| >= 0.4 AND |z_4h (42 bar)| >= 0.3 AND sign(z_1h) == sign(z_4h) == target_dir. **Smart breakeven trail (H1921 transfer)**: 진입 후 unrealized PnL (mark 기준) >= +25bps 도달 시 SL → entry +0bps (breakeven) 이동. 단방향 trail (1회만 발동). hit_breakeven flag 영속화로 restart 후 중복 방지.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +60bps / 초기 SL -40bps → +25bps 도달 시 SL +0bps (breakeven) trail / hold 60min / cooldown 2h. BN futures MAKER REBATE -1bps + slip 2bps (round-trip 2bps, BN VIP1+ tier 가정). 백테스트 50일 n=47 (3코인 합산), WR 59.6%, PF **2.60**, +168K KRW on 100M KRW. 30일 n=29, WR 62.1%, PF 2.79 (V49 최강 WR). 진입일 2026-06-08. sim_h1933_h1881_smart_trail 참조.",
  },

  h1933_aave: {
    name: "H1933 AAVE Cross-coin Basis_delta Divergence 1h + Smart Breakeven Trail + Rebate",
    paradigm: "Basis_delta cross-coin div + smart trail (1h + Rebate)",
    summary:
      "H1881 AAVE base signal + smart breakeven trail stop. V49 axis 4/5 GO. exit-side lever (trail) NEW transfer — V48 H1921 (liq paradigm) → V49 H1933 (basis paradigm) 입증. 진입 후 +25bps 도달 시 SL → +0bps (breakeven) 이동. H1881 PF 2.45 → H1933 PF 2.60.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH basis_delta 168h rolling z. divergence_z (target z - others mean) |>=1.8| → fade. 1h+4h momentum AND gate. Smart trail: 진입 후 unrealized PnL >= +25bps 도달 시 SL → entry (breakeven) 이동. 단방향 (1회만 발동).",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP +60bps / 초기 SL -40bps → +25bps 도달 시 SL +0bps trail / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=47, WR 59.6%, PF 2.60. 30일 PF 2.79. 진입일 2026-06-08. sim_h1933_h1881_smart_trail 참조.",
  },

  h1933_eth: {
    name: "H1933 ETH Cross-coin Basis_delta Divergence 1h + Smart Breakeven Trail + Rebate",
    paradigm: "Basis_delta cross-coin div + smart trail (1h + Rebate)",
    summary:
      "H1881 ETH base signal + smart breakeven trail stop. V49 axis 4/5 GO. exit-side lever (trail) NEW transfer — V48 H1921 (liq paradigm) → V49 H1933 (basis paradigm) 입증. 진입 후 +25bps 도달 시 SL → +0bps (breakeven) 이동. H1881 PF 2.45 → H1933 PF 2.60.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH basis_delta 168h rolling z. divergence_z (target z - others mean) |>=1.8| → fade. 1h+4h momentum AND gate. Smart trail: 진입 후 unrealized PnL (mark 기준) >= +25bps 도달 시 SL → entry (breakeven) 이동. 단방향 trail.",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. TP +60bps / 초기 SL -40bps → +25bps 도달 시 SL +0bps trail / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=47, WR 59.6%, PF 2.60. 30일 PF 2.79. 진입일 2026-06-08. sim_h1933_h1881_smart_trail 참조.",
  },

  h1843_btc: {
    name: "H1843 BTC Cross-coin Liq Divergence 15min HF + Rebate",
    paradigm: "Liq cross-coin divergence (HF + Rebate)",
    summary:
      "H1712 paradigm 의 HF + Maker rebate variant — BTC 단독 liq cascade + 알트(ETH/SOL) quiet divergence 시 BTC reverse fade. V41 axis 4/5. 15min 평가 사이클(H1712 1h → 4× 가속) + TP/SL/hold/cooldown 가속(50/30bps/30min/1h) + MAKER REBATE -1bps tier. ⚠️ BN VIP1+ tier 가정 (Standard tier 시 PF 1.24).",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ETH z <= 0.5 AND SOL z <= 0.5. signed liq (long-side - short-side) > 0 → SHORT (over-react fade), < 0 → LONG. 15min 평가 사이클로 H1712 의 1h cycle 을 4× 가속 + maker rebate 로 비용 흡수. H1843 paradigm 의 HF + rebate 가속 검증.",
    entryExit:
      "BTC HOT + alts COOL → BTC reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps, rebate 수익 가능). 백테스트 50일 n=88, WR 54.5%, PF 2.40, +197K KRW on 100M KRW (BTC/ETH/SOL 합산). 30일 n=65, WR 52.3%, PF 2.20, +133K KRW. Standard fee tier 시 PF 1.24 → rebate tier 의존성 강함. sim_h1843 참조.",
  },

  h1843_eth: {
    name: "H1843 ETH Cross-coin Liq Divergence 15min HF + Rebate",
    paradigm: "Liq cross-coin divergence (HF + Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 발생 시 ETH reverse fade. V41 axis 4/5 HF + rebate. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG (over-react fade). 15min 평가 사이클 + maker rebate 로 H1712 1h cycle 가속화.",
    entryExit:
      "BTC COOL + ETH HOT → ETH reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=88 합산 PF 2.40 WR 54.5% +197K KRW (3코인 합산 통계). sim_h1843 참조.",
  },

  h1843_sol: {
    name: "H1843 SOL Cross-coin Liq Divergence 15min HF + Rebate",
    paradigm: "Liq cross-coin divergence (HF + Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 발생 시 SOL reverse fade. V41 axis 4/5 HF + rebate. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL 각 코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. cross-section liq divergence + 15min HF + maker rebate 가속화.",
    entryExit:
      "BTC COOL + SOL HOT → SOL reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=88 합산 PF 2.40 WR 54.5% +197K KRW (3코인 합산 통계). sim_h1843 참조.",
  },

  h1870_btc: {
    name: "H1870 BTC Cross-coin Liq Divergence 15min HF + Rebate (5-coin)",
    paradigm: "Liq cross-coin divergence 5coin (HF + Rebate)",
    summary:
      "H1843 paradigm 의 universe extension variant — BTC 단독 liq cascade + ETH/SOL/XRP/ADA 4 알트 quiet divergence 시 BTC reverse fade. V43 axis 1/5. 15min HF 사이클 + MAKER REBATE tier. universe 를 BTC/ETH/SOL 3코인 → 5코인 (XRP/ADA 추가) 확장하여 cross-section signal 발화 ↑. ⚠️ BN VIP1+ tier 가정 (Standard tier 시 PF 미달).",
    mechanism:
      "BTC/ETH/SOL/XRP/ADA 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/XRP/ADA) z <= 0.5. signed liq (long-side - short-side) > 0 → SHORT (over-react fade), < 0 → LONG. 4 alts AND-quiet 조건은 case1 엄격하나, XRP/ADA 추가로 alt side trigger 빈도 증가 보완.",
    entryExit:
      "BTC HOT + 4 alts COOL → BTC reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps, rebate 수익 가능). 백테스트 50일 n=125, WR 56.8%, PF 2.30, +166K KRW on 100M KRW. 30일 n=95, WR 55.8%, PF 2.17, +119K KRW. 30d→50d PF 2.17→2.30 안정 + 개선. low variance (PnL std 2.94 USD, max loss -4.78 USD). sim_h1870 참조.",
  },

  h1870_eth: {
    name: "H1870 ETH Cross-coin Liq Divergence 15min HF + Rebate (5-coin)",
    paradigm: "Liq cross-coin divergence 5coin (HF + Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade. V43 axis 1/5 — H1843 paradigm 의 5-coin universe extension. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/XRP/ADA 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG (over-react fade). H1843 의 ETH variant 와 동일하지만 universe 가 5코인으로 확장되어 BTC quiet 정의 변동 없음 (BTC anchor 유지).",
    entryExit:
      "BTC COOL + ETH HOT → ETH reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=125 합산 PF 2.30 WR 56.8% +166K KRW (5코인 합산 통계). sim_h1870 참조.",
  },

  h1870_sol: {
    name: "H1870 SOL Cross-coin Liq Divergence 15min HF + Rebate (5-coin)",
    paradigm: "Liq cross-coin divergence 5coin (HF + Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade. V43 axis 1/5 — H1843 paradigm 의 5-coin universe extension. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/XRP/ADA 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. cross-section liq divergence + 5-coin universe + 15min HF + maker rebate.",
    entryExit:
      "BTC COOL + SOL HOT → SOL reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=125 합산 PF 2.30 WR 56.8% +166K KRW (5코인 합산 통계). sim_h1870 참조.",
  },

  h1870_xrp: {
    name: "H1870 XRP Cross-coin Liq Divergence 15min HF + Rebate (5-coin)",
    paradigm: "Liq cross-coin divergence 5coin (HF + Rebate)",
    summary:
      "XRP 단독 liq cascade + BTC quiet divergence 시 XRP reverse fade. V43 axis 1/5 신규 universe — H1843 (BTC/ETH/SOL) 에서 XRP 추가 확장. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/XRP/ADA 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND XRP z >= 1.5. signed liq > 0 → XRP SHORT, < 0 → XRP LONG. major alt (high liquidity) 의 cross-section liq panic fade — V42 mid-cap universe NO-GO 와 차별점 (BTC anchor 유지 + major alt only).",
    entryExit:
      "BTC COOL + XRP HOT → XRP reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=125 합산 PF 2.30 WR 56.8% +166K KRW (5코인 합산 통계). sim_h1870 참조.",
  },

  h1870_ada: {
    name: "H1870 ADA Cross-coin Liq Divergence 15min HF + Rebate (5-coin)",
    paradigm: "Liq cross-coin divergence 5coin (HF + Rebate)",
    summary:
      "ADA 단독 liq cascade + BTC quiet divergence 시 ADA reverse fade. V43 axis 1/5 신규 universe — H1843 (BTC/ETH/SOL) 에서 ADA 추가 확장. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/XRP/ADA 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ADA z >= 1.5. signed liq > 0 → ADA SHORT, < 0 → ADA LONG. major alt (high liquidity) 의 cross-section liq panic fade — V42 mid-cap universe NO-GO 와 차별점 (BTC anchor 유지 + major alt only).",
    entryExit:
      "BTC COOL + ADA HOT → ADA reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=125 합산 PF 2.30 WR 56.8% +166K KRW (5코인 합산 통계). sim_h1870 참조.",
  },

  h1944_btc: {
    name: "H1944 BTC Cross-coin Liq Divergence 5-coin (BNB/LINK + Rebate)",
    paradigm: "Liq cross-coin div 5-coin v2 (BNB/LINK + Rebate)",
    summary:
      "H1843 paradigm 의 BNB/LINK universe extension — BTC 단독 liq cascade + ETH/SOL/BNB/LINK 4 알트 quiet divergence 시 BTC reverse fade. V50 axis 5/5 GO — V31-V50 후보 중 **최고 PF 3.08**. H1870 (XRP/ADA) 과 직교하는 BNB/LINK 추가. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 각 코인 5min liq notional 의 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/BNB/LINK) z <= 0.5. signed liq > 0 → SHORT (over-react fade), < 0 → LONG. H1870 universe (XRP/ADA) 와 직교 — BNB exchange-token + LINK oracle 추가로 cross-section signal source 다각화.",
    entryExit:
      "BTC HOT(z≥1.5) + 4 alts COOL(z≤0.5) → BTC reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps, rebate 수익 가능). 백테스트 50일 n=86, WR 60.5%, PF **3.08** (V31-V50 최고), +165K KRW on 100M KRW. 30일 n=66, WR 60.6%, PF 2.99, +120K KRW. 30d→50d 모두 안정 + 개선. 진입일 2026-06-08. sim_h1944_h1843_bnb_link_universe 참조.",
  },

  h1944_eth: {
    name: "H1944 ETH Cross-coin Liq Divergence 5-coin (BNB/LINK + Rebate)",
    paradigm: "Liq cross-coin div 5-coin v2 (BNB/LINK + Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade. V50 axis 5/5 — H1843 paradigm 의 BNB/LINK universe extension (H1870 XRP/ADA 와 직교). V31-V50 후보 중 최고 PF 3.08. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG (over-react fade). 5-coin universe 확장으로 BTC quiet 정의는 동일하나 alt side trigger 빈도/품질 ↑.",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=86 합산 WR 60.5% PF 3.08 +165K KRW (5코인 합산 통계). 진입일 2026-06-08. sim_h1944_h1843_bnb_link_universe 참조.",
  },

  h1944_sol: {
    name: "H1944 SOL Cross-coin Liq Divergence 5-coin (BNB/LINK + Rebate)",
    paradigm: "Liq cross-coin div 5-coin v2 (BNB/LINK + Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade. V50 axis 5/5 — H1843 paradigm 의 BNB/LINK universe extension. V31-V50 후보 중 최고 PF 3.08. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. cross-section liq divergence + 5-coin (BNB/LINK) universe + 15min HF + maker rebate.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=86 합산 WR 60.5% PF 3.08 +165K KRW (5코인 합산 통계). 진입일 2026-06-08. sim_h1944_h1843_bnb_link_universe 참조.",
  },

  h1944_bnb: {
    name: "H1944 BNB Cross-coin Liq Divergence 5-coin (BNB/LINK + Rebate)",
    paradigm: "Liq cross-coin div 5-coin v2 (BNB/LINK + Rebate)",
    summary:
      "BNB 단독 liq cascade + BTC quiet divergence 시 BNB reverse fade. V50 axis 5/5 신규 universe — H1843 (BTC/ETH/SOL) 에서 BNB 추가 확장 (H1870 XRP/ADA 와 직교). exchange-token 특유의 거래소 이벤트 driven liq panic capture. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND BNB z >= 1.5. signed liq > 0 → BNB SHORT, < 0 → BNB LONG. exchange-token cross-section liq panic fade — Binance 거래소 이벤트/공지 driven panic 패턴을 BTC anchor 기준으로 분리.",
    entryExit:
      "BTC COOL(z≤0.5) + BNB HOT(z≥1.5) → BNB reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=86 합산 WR 60.5% PF 3.08 +165K KRW (5코인 합산 통계). 진입일 2026-06-08. sim_h1944_h1843_bnb_link_universe 참조.",
  },

  h1944_link: {
    name: "H1944 LINK Cross-coin Liq Divergence 5-coin (BNB/LINK + Rebate)",
    paradigm: "Liq cross-coin div 5-coin v2 (BNB/LINK + Rebate)",
    summary:
      "LINK 단독 liq cascade + BTC quiet divergence 시 LINK reverse fade. V50 axis 5/5 신규 universe — H1843 (BTC/ETH/SOL) 에서 LINK 추가 확장 (H1870 XRP/ADA 와 직교). oracle 코인 특유의 DeFi 이벤트 driven liq panic capture. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND LINK z >= 1.5. signed liq > 0 → LINK SHORT, < 0 → LINK LONG. oracle 코인 cross-section liq panic fade — DeFi/oracle 이벤트 driven panic 을 BTC anchor 기준 분리 (H1870 의 XRP/ADA payment 코인과 직교).",
    entryExit:
      "BTC COOL(z≤0.5) + LINK HOT(z≥1.5) → LINK reverse 진입. TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps. 백테스트 50일 pool n=86 합산 WR 60.5% PF 3.08 +165K KRW (5코인 합산 통계). 진입일 2026-06-08. sim_h1944_h1843_bnb_link_universe 참조.",
  },

  h1951_btc: {
    name: "H1951 BTC Liq 5-coin v2 + Smart Trail (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + smart trail (BNB/LINK + Rebate)",
    summary:
      "H1944 paradigm (5-coin BTC/ETH/SOL/BNB/LINK) + H1921 smart breakeven trail combination. V51 axis 2/5 GO — V31-V51 후보 중 **최고 PF 3.16**. BTC HOT + 4 알트 COOL divergence 시 BTC reverse fade + 진입 후 +20bps 도달 시 SL → +0bps (breakeven) 이동. 두 GO lever combination 입증 (universe extension + exit-side breakeven trail). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/BNB/LINK) z <= 0.5. signed liq > 0 → SHORT, < 0 → LONG. Smart trail: 진입 후 unrealized PnL +20bps 도달 시 SL 을 entry (+0bps breakeven) 로 이동 (단방향 trail — 한번 발동 후 추가 이동 X). hit_breakeven flag 영속화 (재시작 복구 지원).",
    entryExit:
      "BTC HOT(z≥1.5) + 4 alts COOL(z≤0.5) → BTC reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps (breakeven) trail / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps). 백테스트 50일 n=86, WR 53.5%, PF **3.16** (V31-V51 최고). 30일 n=66, WR 51.5%, PF 3.14 — 30d↔50d 매우 안정 (PF 차이 0.02). 진입일 2026-06-08. sim_h1951_h1944_smart_trail 참조.",
  },

  h1951_eth: {
    name: "H1951 ETH Liq 5-coin v2 + Smart Trail (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + smart trail (BNB/LINK + Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade + smart breakeven trail. V51 axis 2/5 — H1944 5-coin + H1921 trail combination, V31-V51 최고 PF 3.16. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG. Smart trail: 진입 후 +20bps 도달 시 SL → entry (+0bps breakeven). 단방향 trail.",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps (BN VIP1+ 가정). 50일 pool n=86 WR 53.5% PF 3.16 (5코인 합산 통계, V31-V51 최고). 30일 PF 3.14 안정. 진입일 2026-06-08. sim_h1951_h1944_smart_trail 참조.",
  },

  h1951_sol: {
    name: "H1951 SOL Liq 5-coin v2 + Smart Trail (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + smart trail (BNB/LINK + Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade + smart breakeven trail. V51 axis 2/5 — H1944 5-coin + H1921 trail combination, V31-V51 최고 PF 3.16. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. Smart trail: 진입 후 +20bps 도달 시 SL → entry (+0bps breakeven). 단방향 trail.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 53.5% PF 3.16 (V31-V51 최고). 30일 PF 3.14 안정. 진입일 2026-06-08. sim_h1951_h1944_smart_trail 참조.",
  },

  h1951_bnb: {
    name: "H1951 BNB Liq 5-coin v2 + Smart Trail (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + smart trail (BNB/LINK + Rebate)",
    summary:
      "BNB 단독 liq cascade + BTC quiet divergence 시 BNB reverse fade + smart breakeven trail. V51 axis 2/5 — H1944 5-coin universe extension + H1921 trail. exchange-token 이벤트 driven liq panic capture + winner preservation. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND BNB z >= 1.5. signed liq > 0 → BNB SHORT, < 0 → BNB LONG. exchange-token cross-section liq panic fade (Binance 거래소 이벤트 driven) + smart trail로 winner preservation.",
    entryExit:
      "BTC COOL(z≤0.5) + BNB HOT(z≥1.5) → BNB reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 53.5% PF 3.16 (V31-V51 최고). 30일 PF 3.14 안정. 진입일 2026-06-08. sim_h1951_h1944_smart_trail 참조.",
  },

  h1951_link: {
    name: "H1951 LINK Liq 5-coin v2 + Smart Trail (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + smart trail (BNB/LINK + Rebate)",
    summary:
      "LINK 단독 liq cascade + BTC quiet divergence 시 LINK reverse fade + smart breakeven trail. V51 axis 2/5 — H1944 5-coin universe extension + H1921 trail. oracle 코인 DeFi 이벤트 driven liq panic capture + winner preservation. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND LINK z >= 1.5. signed liq > 0 → LINK SHORT, < 0 → LINK LONG. oracle 코인 cross-section liq panic fade (DeFi 이벤트 driven) + smart trail로 winner preservation.",
    entryExit:
      "BTC COOL(z≤0.5) + LINK HOT(z≥1.5) → LINK reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 53.5% PF 3.16 (V31-V51 최고). 30일 PF 3.14 안정. 진입일 2026-06-08. sim_h1951_h1944_smart_trail 참조.",
  },

  h1952_btc: {
    name: "H1952 BTC Liq 5-coin v2 + Dynamic Sizing (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + dynamic sizing (BNB/LINK + Rebate)",
    summary:
      "H1944 paradigm (5-coin BTC/ETH/SOL/BNB/LINK) + H1902 dynamic position sizing transfer. V51 axis 3/5 GO — 50일 PF 2.91, 30일 PF 2.83 안정. BTC HOT + 4 알트 COOL divergence 시 BTC reverse fade. 강한 z signal 시 최대 2× position (size = clip((|z|-1.2)/0.5, 1.0, 2.0) × base 5%). **Sizing lever paradigm-agnostic transfer 입증** — basis_delta → liq cross-section. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/BNB/LINK) z <= 0.5. signed liq > 0 → SHORT, < 0 → LONG. Dynamic sizing: sizeMult = clip((|trigger_z|-1.2)/0.5, 1.0, 2.0). |z|=1.5→1.0×, |z|=2.0→1.6×, |z|=2.2→2.0× (cap). 강한 signal 시 winner 강조.",
    entryExit:
      "BTC HOT(z≥1.5) + 4 alts COOL(z≤0.5) → BTC reverse 진입 (size 1.0~2.0× base). TP +50bps / SL -30bps / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps). 백테스트 50일 n=86, WR 60.5%, PF **2.91** (5코인 합산). 30일 n=66, PF 2.83 — 30d↔50d 안정. 진입일 2026-06-09. sim_h1952_h1944_dynamic_sizing 참조.",
  },

  h1952_eth: {
    name: "H1952 ETH Liq 5-coin v2 + Dynamic Sizing (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + dynamic sizing (BNB/LINK + Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade + dynamic sizing. V51 axis 3/5 — H1944 5-coin + H1902 sizing transfer combination, 50일 PF 2.91. 강한 ETH z signal 시 최대 2× position. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG. Dynamic sizing: sizeMult = clip((|ETH z|-1.2)/0.5, 1.0, 2.0). 강한 ETH signal 시 winner 강조.",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입 (size 1.0~2.0× base). TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 60.5% PF 2.91. 30일 PF 2.83 안정. 진입일 2026-06-09. sim_h1952_h1944_dynamic_sizing 참조.",
  },

  h1952_sol: {
    name: "H1952 SOL Liq 5-coin v2 + Dynamic Sizing (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + dynamic sizing (BNB/LINK + Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade + dynamic sizing. V51 axis 3/5 — H1944 5-coin + H1902 sizing combination, 50일 PF 2.91. 강한 SOL z signal 시 최대 2× position. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. Dynamic sizing: sizeMult = clip((|SOL z|-1.2)/0.5, 1.0, 2.0). 강한 SOL signal 시 winner 강조.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입 (size 1.0~2.0× base). TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 60.5% PF 2.91. 30일 PF 2.83 안정. 진입일 2026-06-09. sim_h1952_h1944_dynamic_sizing 참조.",
  },

  h1952_bnb: {
    name: "H1952 BNB Liq 5-coin v2 + Dynamic Sizing (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + dynamic sizing (BNB/LINK + Rebate)",
    summary:
      "BNB 단독 liq cascade + BTC quiet divergence 시 BNB reverse fade + dynamic sizing. V51 axis 3/5 — H1944 5-coin universe extension + H1902 sizing. exchange-token 이벤트 driven liq panic capture + 강한 signal 시 최대 2× position. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND BNB z >= 1.5. signed liq > 0 → BNB SHORT, < 0 → BNB LONG. exchange-token cross-section liq panic fade + dynamic sizing (sizeMult clip((|BNB z|-1.2)/0.5, 1.0, 2.0)).",
    entryExit:
      "BTC COOL(z≤0.5) + BNB HOT(z≥1.5) → BNB reverse 진입 (size 1.0~2.0× base). TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 60.5% PF 2.91. 30일 PF 2.83 안정. 진입일 2026-06-09. sim_h1952_h1944_dynamic_sizing 참조.",
  },

  h1952_link: {
    name: "H1952 LINK Liq 5-coin v2 + Dynamic Sizing (BNB/LINK + Rebate)",
    paradigm: "Liq 5-coin v2 + dynamic sizing (BNB/LINK + Rebate)",
    summary:
      "LINK 단독 liq cascade + BTC quiet divergence 시 LINK reverse fade + dynamic sizing. V51 axis 3/5 — H1944 5-coin universe extension + H1902 sizing. oracle 코인 DeFi 이벤트 driven liq panic capture + 강한 signal 시 최대 2× position. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND LINK z >= 1.5. signed liq > 0 → LINK SHORT, < 0 → LINK LONG. oracle 코인 cross-section liq panic fade + dynamic sizing (sizeMult clip((|LINK z|-1.2)/0.5, 1.0, 2.0)).",
    entryExit:
      "BTC COOL(z≤0.5) + LINK HOT(z≥1.5) → LINK reverse 진입 (size 1.0~2.0× base). TP +50bps / SL -30bps / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=86 WR 60.5% PF 2.91. 30일 PF 2.83 안정. 진입일 2026-06-09. sim_h1952_h1944_dynamic_sizing 참조.",
  },

  h1973_btc: {
    name: "H1973 BTC Liq 5-coin v2 + Trail + 15min Cooldown (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + 15min cooldown (Rebate)",
    summary:
      "H1951 paradigm (5-coin BTC/ETH/SOL/BNB/LINK + smart trail) + cooldown 단축 (1h → 15min). V53 axis 4/5 GO — 50일 n=98 (H1951=86, ~14%↑), WR 53.1%, PF 2.98. 짧은 cooldown으로 같은 코인 4배 trigger 가능 + alpha 유지 검증. H1951 동일 signal mechanism + cooldown 만 변경한 sensitivity test. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/BNB/LINK) z <= 0.5. signed liq > 0 → SHORT, < 0 → LONG. Smart trail (H1951 동일): 진입 후 +20bps 도달 시 SL → entry (+0bps breakeven), 단방향 trail. cooldown 1h → 15min 단축.",
    entryExit:
      "BTC HOT(z≥1.5) + 4 alts COOL(z≤0.5) → BTC reverse 진입. TP +50bps / 초기 SL -30bps → +20bps 도달 시 SL +0bps (breakeven) trail / hold 30min / **cooldown 15min** (H1951=60min). BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps). 백테스트 50일 n=98, WR 53.1%, PF 2.98. 진입일 2026-06-09. sim_h1973_h1951_cooldown_15min 참조.",
  },

  h1973_eth: {
    name: "H1973 ETH Liq 5-coin v2 + Trail + 15min Cooldown (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + 15min cooldown (Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade + smart trail + cooldown 15min. V53 axis 4/5 — H1951 base + cooldown 단축, 50일 PF 2.98. 짧은 cooldown으로 ETH 코인 4배 trigger 가능. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG. Smart trail: 진입 후 +20bps 도달 시 SL → +0bps breakeven. cooldown 15min (H1951=1h).",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입. TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / **cooldown 15min**. MAKER REBATE -1bps + slip 1bps. 50일 pool n=98 WR 53.1% PF 2.98. 진입일 2026-06-09. sim_h1973_h1951_cooldown_15min 참조.",
  },

  h1973_sol: {
    name: "H1973 SOL Liq 5-coin v2 + Trail + 15min Cooldown (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + 15min cooldown (Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade + smart trail + cooldown 15min. V53 axis 4/5 — H1951 base + cooldown 단축, 50일 PF 2.98. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. Smart trail: 진입 후 +20bps 도달 시 SL → +0bps breakeven. cooldown 15min.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입. TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / **cooldown 15min**. MAKER REBATE -1bps + slip 1bps. 50일 pool n=98 WR 53.1% PF 2.98. 진입일 2026-06-09. sim_h1973_h1951_cooldown_15min 참조.",
  },

  h1973_bnb: {
    name: "H1973 BNB Liq 5-coin v2 + Trail + 15min Cooldown (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + 15min cooldown (Rebate)",
    summary:
      "BNB 단독 liq cascade + BTC quiet divergence 시 BNB reverse fade + smart trail + cooldown 15min. V53 axis 4/5 — H1951 base + cooldown 단축. exchange-token 이벤트 driven liq panic capture, 짧은 cooldown으로 trigger 빈도 증가. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND BNB z >= 1.5. signed liq > 0 → BNB SHORT, < 0 → BNB LONG. Smart trail + cooldown 15min.",
    entryExit:
      "BTC COOL(z≤0.5) + BNB HOT(z≥1.5) → BNB reverse 진입. TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / **cooldown 15min**. MAKER REBATE -1bps + slip 1bps. 50일 pool n=98 WR 53.1% PF 2.98. 진입일 2026-06-09. sim_h1973_h1951_cooldown_15min 참조.",
  },

  h1973_link: {
    name: "H1973 LINK Liq 5-coin v2 + Trail + 15min Cooldown (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + 15min cooldown (Rebate)",
    summary:
      "LINK 단독 liq cascade + BTC quiet divergence 시 LINK reverse fade + smart trail + cooldown 15min. V53 axis 4/5 — H1951 base + cooldown 단축. oracle 코인 DeFi 이벤트 driven liq panic capture, 짧은 cooldown으로 trigger 빈도 증가. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND LINK z >= 1.5. signed liq > 0 → LINK SHORT, < 0 → LINK LONG. Smart trail + cooldown 15min.",
    entryExit:
      "BTC COOL(z≤0.5) + LINK HOT(z≥1.5) → LINK reverse 진입. TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / **cooldown 15min**. MAKER REBATE -1bps + slip 1bps. 50일 pool n=98 WR 53.1% PF 2.98. 진입일 2026-06-09. sim_h1973_h1951_cooldown_15min 참조.",
  },

  h1991_btc: {
    name: "H1991 BTC Liq 5-coin v2 + Trail + Extreme-Z 4× Ladder (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + extreme-z 4× sizing (Rebate)",
    summary:
      "H1951 paradigm (5-coin BTC/ETH/SOL/BNB/LINK + smart trail) + extreme-z 3-step ladder sizing (1.0× / 2.0× / 4.0×). V54 axis GO — 50일 n=84, WR 53.6%, PF 2.81, +254 USD. BTC HOT + 4 알트 COOL divergence 시 BTC reverse fade. Extreme-z (|z|≥2.5) 시 4× position 으로 큰 reversal capture 강조. H1952 단조 증가 sizing 과 다른 ladder mechanism. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case1 트리거: BTC z >= 1.5 AND ALL 4 alts (ETH/SOL/BNB/LINK) z <= 0.5. signed liq > 0 → SHORT, < 0 → LONG. Smart trail: 진입 후 +20bps 도달 시 SL → entry (+0bps breakeven). Extreme-z ladder: |z|<2.0 → 1.0×, |z|≥2.0 → 2.0×, |z|≥2.5 → 4.0× base. Extreme-z historical reversal capture 강조.",
    entryExit:
      "BTC HOT(z≥1.5) + 4 alts COOL(z≤0.5) → BTC reverse 진입 (size 1.0/2.0/4.0× ladder). TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / cooldown 1h. BN futures MAKER REBATE -1bps + slip 1bps (round-trip 0bps). 백테스트 50일 n=84, WR 53.6%, PF **2.81**, +254 USD on 100M KRW. 진입일 2026-06-09. sim_h1991_h1951_extreme_z_ladder 참조.",
  },

  h1991_eth: {
    name: "H1991 ETH Liq 5-coin v2 + Trail + Extreme-Z 4× Ladder (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + extreme-z 4× sizing (Rebate)",
    summary:
      "ETH 단독 liq cascade + BTC quiet divergence 시 ETH reverse fade + smart trail + extreme-z 4× ladder. V54 axis GO — H1951 base + ladder sizing, 50일 PF 2.81. Extreme-z (|z|≥2.5) 시 4× position. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND ETH z >= 1.5. signed liq > 0 → ETH SHORT, < 0 → ETH LONG. Smart trail + extreme-z ladder: |ETH z|<2.0 → 1.0×, |z|≥2.0 → 2.0×, |z|≥2.5 → 4.0× base.",
    entryExit:
      "BTC COOL(z≤0.5) + ETH HOT(z≥1.5) → ETH reverse 진입 (size 1.0/2.0/4.0× ladder). TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=84 WR 53.6% PF 2.81 +254 USD. 진입일 2026-06-09. sim_h1991_h1951_extreme_z_ladder 참조.",
  },

  h1991_sol: {
    name: "H1991 SOL Liq 5-coin v2 + Trail + Extreme-Z 4× Ladder (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + extreme-z 4× sizing (Rebate)",
    summary:
      "SOL 단독 liq cascade + BTC quiet divergence 시 SOL reverse fade + smart trail + extreme-z 4× ladder. V54 axis GO — H1951 base + ladder sizing, 50일 PF 2.81. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND SOL z >= 1.5. signed liq > 0 → SOL SHORT, < 0 → SOL LONG. Smart trail + extreme-z ladder.",
    entryExit:
      "BTC COOL(z≤0.5) + SOL HOT(z≥1.5) → SOL reverse 진입 (size 1.0/2.0/4.0× ladder). TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=84 WR 53.6% PF 2.81. 진입일 2026-06-09. sim_h1991_h1951_extreme_z_ladder 참조.",
  },

  h1991_bnb: {
    name: "H1991 BNB Liq 5-coin v2 + Trail + Extreme-Z 4× Ladder (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + extreme-z 4× sizing (Rebate)",
    summary:
      "BNB 단독 liq cascade + BTC quiet divergence 시 BNB reverse fade + smart trail + extreme-z 4× ladder. V54 axis GO — exchange-token 이벤트 driven liq panic capture + extreme-z capital allocation. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND BNB z >= 1.5. signed liq > 0 → BNB SHORT, < 0 → BNB LONG. Smart trail + extreme-z ladder.",
    entryExit:
      "BTC COOL(z≤0.5) + BNB HOT(z≥1.5) → BNB reverse 진입 (size 1.0/2.0/4.0× ladder). TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=84 WR 53.6% PF 2.81. 진입일 2026-06-09. sim_h1991_h1951_extreme_z_ladder 참조.",
  },

  h1991_link: {
    name: "H1991 LINK Liq 5-coin v2 + Trail + Extreme-Z 4× Ladder (Rebate)",
    paradigm: "Liq 5-coin v2 + trail + extreme-z 4× sizing (Rebate)",
    summary:
      "LINK 단독 liq cascade + BTC quiet divergence 시 LINK reverse fade + smart trail + extreme-z 4× ladder. V54 axis GO — oracle 코인 DeFi 이벤트 driven liq panic capture + extreme-z capital allocation. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "BTC/ETH/SOL/BNB/LINK 5코인 5min liq notional 24h rolling z-score (288 bars). case2 트리거: BTC z <= 0.5 AND LINK z >= 1.5. signed liq > 0 → LINK SHORT, < 0 → LINK LONG. Smart trail + extreme-z ladder.",
    entryExit:
      "BTC COOL(z≤0.5) + LINK HOT(z≥1.5) → LINK reverse 진입 (size 1.0/2.0/4.0× ladder). TP +50bps / 초기 SL -30bps → +20bps SL +0bps trail / hold 30min / cooldown 1h. MAKER REBATE -1bps + slip 1bps. 50일 pool n=84 WR 53.6% PF 2.81. 진입일 2026-06-09. sim_h1991_h1951_extreme_z_ladder 참조.",
  },

  h2012_doge: {
    name: "H2012 DOGE Cross-coin Basis_delta Divergence (1h + Rebate, Cooldown 1h)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate, cooldown 1h)",
    summary:
      "H1881 (V44 cross-coin basis_delta divergence + rebate) base 그대로 + **cooldown 단축 2h → 1h**. V55 GO. cooldown 단축으로 신호 capture 증가 (n 45 → 47) + WR 향상 (57.8% → 59.6%) + PF 향상 (2.45 → 2.60). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "H1881 signal 동일 — DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z-score. divergence = DOGE z - mean(AAVE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate(|z_1h|>=0.4 ∧ |z_4h|>=0.3) 통과 시 DOGE fade. 유일한 차이는 cooldown 7200s → 3600s 단축 — 같은 코인에서 1h 후 재진입 허용.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). TP +60bps / SL -40bps / hold 60min / **cooldown 1h** (H1881 2h 에서 단축). BN futures MAKER REBATE -1bps + slip 2bps (round-trip 2bps, BN VIP1+ tier 가정). 백테스트 50일 n=47 (3코인 합산), WR 59.6%, PF 2.60. 진입일 2026-06-09. sim_h2012 참조.",
  },

  h2012_aave: {
    name: "H2012 AAVE Cross-coin Basis_delta Divergence (1h + Rebate, Cooldown 1h)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate, cooldown 1h)",
    summary:
      "H1881 AAVE base + cooldown 단축 2h → 1h. V55 GO. AAVE 5 framework (H1502/H1530/H1543) GO 패턴 + cross-section + rebate variant + cooldown 단축.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = AAVE z - mean(DOGE z, ETH z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 AAVE fade 진입. H1881 동일 mechanism + cooldown 1h 단축.",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. TP +60bps / SL -40bps / hold 60min / **cooldown 1h** (H1881 2h 에서 단축). MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=47, WR 59.6%, PF 2.60. 진입일 2026-06-09. sim_h2012 참조.",
  },

  h2012_eth: {
    name: "H2012 ETH Cross-coin Basis_delta Divergence (1h + Rebate, Cooldown 1h)",
    paradigm: "Basis_delta cross-coin div (1h + Rebate, cooldown 1h)",
    summary:
      "H1881 ETH base + cooldown 단축 2h → 1h. V55 GO. H1320 (ETH basis paper GO) + cross-section + rebate variant + cooldown 단축.",
    mechanism:
      "DOGE/AAVE/ETH 각 코인 basis_delta 168h rolling z. divergence = ETH z - mean(DOGE z, AAVE z) 임계치 |z|>=1.8 + 1h/4h momentum AND gate 통과 시 ETH fade 진입. H1881 동일 mechanism + cooldown 1h 단축.",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. TP +60bps / SL -40bps / hold 60min / **cooldown 1h** (H1881 2h 에서 단축). MAKER REBATE -1bps + slip 2bps (BN VIP1+ tier 가정). 50일 pool n=47, WR 59.6%, PF 2.60. 진입일 2026-06-09. sim_h2012 참조.",
  },

  h1994_doge: {
    name: "H1994 DOGE Cross-coin Basis Delta + Sizing + Trail (Rebate)",
    paradigm: "Basis_delta cross-coin + 3-lever combination (Rebate)",
    summary:
      "H1881 paradigm (cross-coin basis_delta divergence + rebate, PF 2.45) + H1902 dynamic sizing (PF 2.52) + H1933 smart trail (PF 2.60) → 3-lever GO combination. V54 axis GO — 50일 n=47, WR 59.6%, PF 2.72, +187 USD on 100M KRW (DOGE/AAVE/ETH). V44/V46/V49 paradigm-agnostic mechanism 융합 — basis paradigm 첫 3-lever combination. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "DOGE/AAVE/ETH 3코인 basis_bps = (futures-spot)/spot*10000. 168h(7d) rolling z-score on delta. divergence = z[target] - mean(z[others]) >= 1.8 → target only fade + multi-TF momentum AND gate (|z_1h|>=0.4 AND |z_4h|>=0.3, same sign). Dynamic sizing: |divergence|<2.0 → 1.0×, |z|>=2.0 → 1.5×, |z|>=2.5 → 2.0× base. Smart trail: 진입 후 +25bps 도달 시 SL → +0bps (breakeven). 단방향 trail.",
    entryExit:
      "divergence_z >= +1.8 → DOGE SHORT, <= -1.8 → DOGE LONG (momentum 부호 일치 필수). 사이즈 1.0/1.5/2.0× ladder. TP +60bps / 초기 SL -40bps → +25bps 도달 시 SL +0bps trail / hold 60min / cooldown 2h. BN futures MAKER REBATE -1bps + slip 2bps (round-trip 2bps). 백테스트 50일 n=47, WR 59.6%, PF **2.72**, +187 USD on 100M KRW (3-lever combination). 진입일 2026-06-09. sim_h1994_h1881_sizing_trail 참조.",
  },

  h1994_aave: {
    name: "H1994 AAVE Cross-coin Basis Delta + Sizing + Trail (Rebate)",
    paradigm: "Basis_delta cross-coin + 3-lever combination (Rebate)",
    summary:
      "AAVE cross-coin basis_delta divergence + dynamic sizing + smart trail. V54 axis GO — H1881 base + H1902 sizing + H1933 trail 3-lever combination, 50일 PF 2.72. AAVE 4-framework alpha 진성 (H1200 메타 ⑥). ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "DOGE/AAVE/ETH 3코인 basis_delta divergence + multi-TF momentum AND gate (H1881 동일). Dynamic sizing 3-step ladder (1.0/1.5/2.0×) + smart breakeven trail (+25bps → +0bps).",
    entryExit:
      "divergence_z >= +1.8 → AAVE SHORT, <= -1.8 → AAVE LONG. 사이즈 1.0/1.5/2.0× ladder. TP +60bps / 초기 SL -40bps → +25bps SL +0bps trail / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps. 50일 pool n=47 PF 2.72 WR 59.6% +187 USD. 진입일 2026-06-09. sim_h1994_h1881_sizing_trail 참조.",
  },

  h1994_eth: {
    name: "H1994 ETH Cross-coin Basis Delta + Sizing + Trail (Rebate)",
    paradigm: "Basis_delta cross-coin + 3-lever combination (Rebate)",
    summary:
      "ETH cross-coin basis_delta divergence + dynamic sizing + smart trail. V54 axis GO — H1881 base + H1902 sizing + H1933 trail 3-lever combination, 50일 PF 2.72. H1320 ETH paper GO cross-confirm. ⚠️ BN VIP1+ tier 가정.",
    mechanism:
      "DOGE/AAVE/ETH 3코인 basis_delta divergence + multi-TF momentum AND gate (H1881 동일). Dynamic sizing 3-step ladder (1.0/1.5/2.0×) + smart breakeven trail (+25bps → +0bps).",
    entryExit:
      "divergence_z >= +1.8 → ETH SHORT, <= -1.8 → ETH LONG. 사이즈 1.0/1.5/2.0× ladder. TP +60bps / 초기 SL -40bps → +25bps SL +0bps trail / hold 60min / cooldown 2h. MAKER REBATE -1bps + slip 2bps. 50일 pool n=47 PF 2.72 WR 59.6% +187 USD. 진입일 2026-06-09. sim_h1994_h1881_sizing_trail 참조.",
  },

  // ─────────────────────────────────────────────────────────
  // 폐기 이력 전략 설명 (Retired — 설명 전용)
  //
  // DAS-126 이후 폐기 상태/사유/최종통계는 백엔드(paper_retired_strategies)가
  // 영속화한다. 여기에는 폐기 카드/디테일에 쓰일 사람이 읽는 설명만 남긴다.
  // (retired / retiredAt / retiredReason / finalStats 필드는 제거됨)
  // ─────────────────────────────────────────────────────────

  h927: {
    name: "H927 매크로 Risk-Off 알트 SHORT",
    paradigm: "Macro",
    summary:
      "VIX/DXY/GOLD risk-off 시그널 알트 SHORT. 2026-06-06 paper 운영 NO-GO 폐기. (paper 5일 n=47, WR 44.7%, PF 0.70, -353K KRW — 백테스트 GO → paper NO-GO 패턴 반복)",
    mechanism:
      "주식시장 변동성(VIX), 달러강세(DXY), 안전자산(GOLD) 3종 동반 상승 → 알트(ETH/SOL/XRP/ADA) 60분 SHORT. V8 cross-asset macro paradigm 의 첫 GO 였으나 paper 6일 PF 0.70 로 백테스트-실거래 괴리 확정.",
    entryExit:
      "VIX/DXY/GOLD 동시 임계치 이상 → 알트 4종 균등 SHORT. 60분 시간청산 또는 +0.5% TP / -0.3% SL.",
  },

  h2021: {
    name: "H2021 매크로 Risk-Off 알트 SHORT (h927 교정판)",
    paradigm: "Macro fade",
    summary:
      "주식 변동성(VIX)·달러(DXY)가 동반 급등하고 안전자산 금(GOLD)이 빠지는 '위험회피(risk-off)' 국면에서, 그때 이미 하락 중인 알트코인만 골라 SHORT(공매도)하는 전략. 거시 악재가 암호화폐로 번지는 흐름을 노린다. 폐기된 h927 에 '가격확인 필터' 와 '4시간 보유' 를 더해 교정한 버전 — 악재 신호만으로 무조건 숏하던 것을, 실제로 약한 코인만 충분한 시간을 두고 숏하도록 개선했다.",
    mechanism:
      "VIX·DXY·GOLD 각각의 z-score(평소 대비 표준편차 이탈도)가 동시에 VIX>+1.5 ∧ DXY>+1.5 ∧ GOLD<-1.5 면 risk-off 국면으로 판정. h927(원본)은 이때 5개 알트(BTC/ETH/SOL/XRP/ADA)를 무조건 60분 SHORT 했으나 페이퍼에서 NO-GO(-1.06M KRW — 단 손실의 2/3는 다운타임에 방치된 포지션을 청산한 운영 사고). 근본 원인 두 가지를 백테스트로 교정 — (1) 가격확인 필터: 직전 1시간 하락 중인 코인만 SHORT 해 상승장(rally)에 거꾸로 숏하는 오신호를 제거, (2) 보유 60분→4시간: 거시 국면은 시간 단위로 전개되는데 60분 청산이 수익을 잘랐음. ⚠️ 거시 신호는 일(日) 단위라 독립 이벤트가 소수(small-n) → 수 주간 거래가 없을(dormant) 수 있고 통계 신뢰도에 한계가 있다.",
    entryExit:
      "진입: VIX z>+1.5 ∧ DXY z>+1.5 ∧ GOLD z<-1.5 + 해당 알트의 직전 1시간 수익률 < 0(이미 약세) → SHORT. 청산: 익절 TP +2.5% / 손절 SL -2.0% / 4시간 시간청산. 5개 알트(BTC/ETH/SOL/XRP/ADA) 균등 비중, 시장가(taker) 비용 12bps. 백테스트(taker): TRAIN 3/16~5/1 WR 64.3% PF 3.01 / OOS 5/1~6/11(페이퍼 실패 구간 포함) WR 75.0% PF 2.51. ⚠️ risk-off 국면에서만 발화 → dormant 가능, 2주 페이퍼 최종검증 중(~6/28).",
  },

  h1106_tight: {
    name: "H1106 매크로 통합 (Tight 변형)",
    paradigm: "Macro",
    summary:
      "H927 매크로 신호 + z-score 임계치 강화 변형. 2026-06-06 paper 운영 NO-GO 폐기. (paper 10일 n=40, WR 47.5%, PF 0.91, -75K KRW — H927 모축 폐기와 동반 정리)",
    mechanism:
      "h1106 시리즈 6종 중 가장 엄격한 진입 조건. H927 동일 매크로 시그널 + z-score 임계치 상향. 백테스트에서 SL 발생률 가장 낮았으나 paper 에서는 H927 폐기와 동일 패턴.",
    entryExit:
      "H927 매크로 시그널 + |z|>2.5 등 강화된 임계치. TP/SL 좁게 설정.",
  },

  h1118: {
    name: "H1118 Vol5m SHORT 시리즈",
    paradigm: "Vol5m",
    summary:
      "5분 변동성 폭증 시 SHORT — 1주 paper trading 결과 NO-GO.",
    mechanism:
      "vol5m z-score 만 단독으로 사용한 가장 단순한 변형. 보조 신호 결합이 없어 노이즈에 약했다.",
    entryExit: "vol5m z>2 → SHORT.",
  },

  h1129: {
    name: "H1129 Vol5m LONG 시리즈",
    paradigm: "Vol5m",
    summary:
      "H1118 의 반대 방향 변형. 1주 paper 결과 동일하게 NO-GO.",
    mechanism:
      "vol5m z<-2 → LONG. 단독 vol5m 의 양방향 모두 noise 압도.",
    entryExit: "vol5m z<-2 → LONG.",
  },

  h1101: {
    name: "H1101 매크로 변형 (lenient)",
    paradigm: "Macro",
    summary:
      "H927 매크로의 변형 중 하나. h1106_tight 단일 유지를 위해 통합 페기.",
    mechanism:
      "H927 매크로 risk-off 시그널의 임계치 변형. h1101/h1105/h1106_lenient/h1106_super/h1106_wide/h1107 6종 모두 trigger 중복 PnL 동일 → 통합 정리.",
    entryExit: "H927 동일 매크로 시그널 + 완화된 임계치.",
  },

  h1105: {
    name: "H1105 매크로 변형 (super)",
    paradigm: "Macro",
    summary:
      "H927 매크로 변형. trigger 중복으로 페기.",
    mechanism: "H927 매크로 시그널의 임계치 super 변형.",
    entryExit: "H927 동일.",
  },

  h1106_lenient: {
    name: "H1106 매크로 변형 (lenient)",
    paradigm: "Macro",
    summary:
      "H1106 매크로 시리즈 중 가장 느슨한 변형. h1106_tight 단일 유지로 페기.",
    mechanism: "h1106 시리즈 중 임계치를 가장 낮게 잡은 변형. SL 발생률이 더 높아 PF 가 tight 변형보다 낮았다.",
    entryExit: "H927 매크로 시그널 + 완화된 임계치.",
  },

  h1106_super: {
    name: "H1106 매크로 변형 (super)",
    paradigm: "Macro",
    summary: "H1106 시리즈 중 super 임계치 변형. 페기.",
    mechanism: "h1106 시리즈 confluence 강화 변형.",
    entryExit: "H927 매크로 시그널 + super 임계치.",
  },

  h1106_wide: {
    name: "H1106 매크로 변형 (wide)",
    paradigm: "Macro",
    summary: "H1106 시리즈 중 wide 임계치 변형. 페기.",
    mechanism: "h1106 시리즈 wide window 변형.",
    entryExit: "H927 매크로 시그널 + wide window.",
  },

  h1107: {
    name: "H1107 매크로 변형 (base)",
    paradigm: "Macro",
    summary: "H927 의 첫 보강 시도. h1106_tight 로 통합 페기.",
    mechanism: "H927 매크로 시그널 + 4단 보강 방법론의 시작점.",
    entryExit: "H927 + 양방향 + 부분청산 + vol regime + z 완화.",
  },

  h1046: {
    name: "H1046 단일 청산 캐스케이드 모멘텀",
    paradigm: "Liq",
    summary:
      "V9 paradigm 의 첫 GO 후보. paper 6일 결과 NO-GO.",
    mechanism:
      "단일 코인에서 청산 폭증 직후 같은 방향 모멘텀을 따라가는 H1007 의 역방향 변형. 백테스트 IS Sharpe +4.48, OOS +3.73 으로 유망했다.",
    entryExit: "liq cascade momentum 신호 + 청산 방향 follow.",
  },

  h1048: {
    name: "H1048 크로스 코인 청산 전염",
    paradigm: "Liq",
    summary:
      "BTC 청산 폭증이 알트 4종으로 전염된다는 가설. paper 자동 손절.",
    mechanism:
      "BTC 에서 청산 폭증이 일어나면 알트 4종(ETH/SOL/XRP/ADA)도 잠시 같은 방향으로 움직이는 contagion 효과를 활용. 백테스트 dir_acc 60~83%.",
    entryExit: "BTC liq z 임계치 → 알트 4종 동시 진입.",
  },

  h1049: {
    name: "H1049 OI 급증 역방향",
    paradigm: "OI",
    summary:
      "Open Interest(미결제약정) 급증 직후 가격이 반대 방향으로 움직인다는 가설. paper 3 trade 만에 NO-GO.",
    mechanism:
      "OI 가 급격히 증가하면 신규 진입이 한 쪽으로 쏠렸다는 신호. 평균회귀에 의해 곧 반대 방향으로 돌아온다는 가정.",
    entryExit: "OI z 임계치 + 가격 방향 역행 진입.",
  },

  h984: {
    name: "H984 BCR-99 axis",
    paradigm: "Variant",
    summary:
      "BCR-99 검증 axis. 백테스트 GO 였으나 paper 결과 NO-GO.",
    mechanism:
      "백테스트 OOS Sharpe 4.88 의 유망 axis 였다. paper 진입 후 실시간 비용/슬리피지가 백테스트 대비 크게 누적되면서 손실.",
    entryExit: "각 변형마다 다름.",
  },
};

/**
 * 전략 id 로 메타를 lookup. 없으면 null.
 * @param {string} id
 * @returns {object | null}
 */
export function getStrategyMeta(id) {
  if (!id) return null;
  return STRATEGY_META[id] ?? null;
}

/**
 * 백엔드(`GET /api/paper/retired`) 폐기 레코드 배열을 strategyMeta 설명과 머지해
 * 폐기 카드/카탈로그가 기대하는 형태로 변환한다. (DAS-126)
 *
 * 백엔드 레코드: { strategy_id, label, labeled_at, reason, final_stats }
 * 결과: { id, name, paradigm, summary, mechanism, entryExit,
 *         retired:true, retiredAt, retiredReason, finalStats }
 *
 * labeled_at 은 KST 벽시계 datetime("YYYY-MM-DD HH:mm:ss", 레거시는 날짜 단위)이며
 * formatLabeledAt 으로 분까지만 노출한다. (이전에는 존재하지 않는 r.retired_at 을
 * 읽어 폐기 시각이 표시되지 않던 버그가 있었다 — DAS-126.)
 *
 * @param {Array<{strategy_id:string, labeled_at:string, reason:string, final_stats:object|null}>} records
 * @returns {Array<{id: string} & object>}
 */
export function mergeRetiredWithMeta(records) {
  if (!Array.isArray(records)) return [];
  return records.map((r) => {
    const meta = STRATEGY_META[r.strategy_id] ?? {};
    return {
      id: r.strategy_id,
      ...meta,
      retired: true,
      retiredAt: formatLabeledAt(r.labeled_at),
      retiredReason: r.reason,
      finalStats: r.final_stats ?? null,
    };
  });
}

/**
 * 백엔드(`GET /api/paper/adopted`) 채택 레코드 배열을 strategyMeta 설명과 머지해
 * 채택 카드/배지가 기대하는 형태로 변환한다. (DAS-126)
 *
 * mergeRetiredWithMeta 와 동일한 구조이되 adopted 플래그를 단다.
 * 백엔드 레코드: { strategy_id, label, labeled_at, reason, final_stats }
 * 결과: { id, name, paradigm, summary, mechanism, entryExit,
 *         adopted:true, adoptedAt, adoptedReason, finalStats }
 *
 * @param {Array<{strategy_id:string, labeled_at:string, reason:string, final_stats:object|null}>} records
 * @returns {Array<{id: string} & object>}
 */
export function mergeAdoptedWithMeta(records) {
  if (!Array.isArray(records)) return [];
  return records.map((r) => {
    const meta = STRATEGY_META[r.strategy_id] ?? {};
    return {
      id: r.strategy_id,
      ...meta,
      adopted: true,
      adoptedAt: formatLabeledAt(r.labeled_at),
      adoptedReason: r.reason,
      finalStats: r.final_stats ?? null,
    };
  });
}

/**
 * 설명(strategyMeta)이 등록된 모든 전략 id 집합 반환.
 * AttentionBar 의 "메타 누락" 경고가 운영 전략과 대조하는 용도.
 * @returns {string[]}
 */
export function getDescribedStrategyIds() {
  return Object.keys(STRATEGY_META);
}

export default STRATEGY_META;
